import React, { useState, useEffect, useRef, useMemo, lazy, Suspense, useCallback } from 'react';
import { isEmbedded } from '../../shared/utils/embedDetection';
import {
    Send, MessageSquare, Search, Plus, User, Users, X, Smile, Paperclip,
    Check, CheckCheck, Loader2, FileText, Image as ImageIcon, Bell, BellOff,
    ArrowDown, Trash2, AlertTriangle, MoreHorizontal, Copy, Pin, PinOff,
    Star, Edit3, Reply, Forward, MessageCircle, AtSign, Bold, Italic,
    Code, Hash, Eye, EyeOff, CornerUpLeft, Mic, Video, Phone, Maximize2,
    UserPlus, UserMinus, LogOut, ChevronUp
} from 'lucide-react';
import { ChatService } from '../services/ChatService';
import { fetchTeamMembers } from '../api/teams';
import LinkPreview from './LinkPreview';
import AudioMessageRecorder from './AudioMessageRecorder';
import VideoMessageRecorder from './VideoMessageRecorder';
import AudioAttachment from './AudioAttachment';
import VideoAttachment from './VideoAttachment';
import ImageAttachment from './ImageAttachment';

import { getUpgradeMessage, getTeamLimit, normalizeTier } from "../utils/subscriptionHelpers";

// Lazy load EmojiPicker for better initial load performance
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Module-level flag to track auth errors across StrictMode remounts
let chatAuthErrorOccurred = false;

// Helper function for relative time display (like Slack)
const getRelativeTime = (timestamp) => {
    const now = new Date();
    const msgTime = new Date(timestamp);
    const diffMs = now - msgTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return msgTime.toLocaleDateString();
};

// Helper to format time for hover tooltip
const getFullTime = (timestamp) => {
    return new Date(timestamp).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Helper to generate message preview text (handles attachments)
const getMessagePreviewText = (message) => {
    if (!message) return 'No messages yet';

    // Check for text content first
    const text = message.text || message.content || message.message;
    if (text && text.trim()) return text;

    // Check for attachments
    const attachments = message.attachments;
    if (attachments && attachments.length > 0) {
        const att = attachments[0];
        const fileType = att.file_type || att.type || att.contentType || '';
        const filename = att.filename || att.name || '';

        if (fileType.startsWith('image/')) {
            return attachments.length > 1 ? `Sent ${attachments.length} images` : 'Sent an image';
        }
        if (fileType.startsWith('video/')) {
            return attachments.length > 1 ? `Sent ${attachments.length} videos` : 'Sent a video';
        }
        if (fileType.startsWith('audio/')) {
            return 'Sent a voice message';
        }
        if (fileType === 'application/pdf') {
            return `Sent: ${filename}`;
        }
        return attachments.length > 1 ? `Sent ${attachments.length} files` : `Sent: ${filename}`;
    }

    // Check for link previews
    if (message.link_previews && message.link_previews.length > 0) {
        return message.link_previews[0].url || 'Shared a link';
    }

    return 'No messages yet';
};

// Helper to get conversation display name (like Slack DMs)
const getConversationDisplayName = (conv, currentUserEmail) => {
    if (conv.name) return conv.name;

    // Filter out the current user for display
    const otherParticipants = conv.participants?.filter(p => p.email !== currentUserEmail) || [];

    if (otherParticipants.length === 0) {
        // Only current user (shouldn't happen, but fallback)
        return conv.participants?.map(p => p.name || p.email.split('@')[0]).join(', ') || 'Chat';
    }

    // Show other participant(s) names
    return otherParticipants.map(p => p.name || p.email.split('@')[0]).join(', ');
};

const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    const size = Number(bytes);
    if (!Number.isFinite(size)) return "";
    const unitIndex = size === 0 ? 0 : Math.min(Math.floor(Math.log10(size) / 3), units.length - 1);
    const value = size / Math.pow(1024, unitIndex);
    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

// Helper to parse and render formatted text (bold, italic, code, mentions, URLs)
const parseFormattedText = (text, participants = []) => {
    if (!text) return text;

    const parts = [];
    let key = 0;
    let lastIndex = 0;
    const allMatches = [];

    // Find all URLs (http, https, or www)
    const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        let url = match[1];
        // Add https:// if URL starts with www
        const href = url.startsWith('www.') ? `https://${url}` : url;
        allMatches.push({
            type: 'url',
            start: match.index,
            end: match.index + match[0].length,
            content: url,
            href: href,
            full: match[0]
        });
    }

    // Find all mentions @username
    const mentionRegex = /@(\w+(?:\.\w+)*(?:@[\w.]+)?)/g;
    while ((match = mentionRegex.exec(text)) !== null) {
        allMatches.push({
            type: 'mention',
            start: match.index,
            end: match.index + match[0].length,
            content: match[1],
            full: match[0]
        });
    }

    // Find formatting: **bold**, *italic*, ```code blocks```, `code`
    const formatRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`{3}([\s\S]+?)`{3}|`(.+?)`)/g;
    while ((match = formatRegex.exec(text)) !== null) {
        let type, content;
        if (match[2]) { type = 'bold'; content = match[2]; }
        else if (match[3]) { type = 'italic'; content = match[3]; }
        else if (match[4]) { type = 'codeblock'; content = match[4]; }
        else if (match[5]) { type = 'code'; content = match[5]; }

        allMatches.push({ type, start: match.index, end: match.index + match[0].length, content, full: match[0] });
    }

    // Sort by start position
    allMatches.sort((a, b) => a.start - b.start);

    allMatches.forEach(m => {
        if (m.start >= lastIndex) {
            if (m.start > lastIndex) {
                parts.push(<span key={key++}>{text.slice(lastIndex, m.start)}</span>);
            }

            if (m.type === 'url') {
                parts.push(
                    <a
                        key={key++}
                        href={m.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:text-sky-300 underline break-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {m.content}
                    </a>
                );
            } else if (m.type === 'mention') {
                const mentionedUser = participants.find(p =>
                    p.email?.toLowerCase().includes(m.content.toLowerCase()) ||
                    p.name?.toLowerCase() === m.content.toLowerCase()
                );
                parts.push(
                    <span key={key++} className="bg-sky-500/20 text-sky-400 px-1 rounded font-medium" title={mentionedUser?.email || m.content}>
                        @{mentionedUser?.name?.split(' ')[0] || m.content}
                    </span>
                );
            } else if (m.type === 'bold') {
                parts.push(<strong key={key++} className="font-bold">{m.content}</strong>);
            } else if (m.type === 'italic') {
                parts.push(<em key={key++} className="italic">{m.content}</em>);
            } else if (m.type === 'codeblock') {
                parts.push(<pre key={key++} className="bg-slate-800 text-slate-200 px-3 py-2 rounded-lg text-xs font-mono my-1 overflow-x-auto">{m.content}</pre>);
            } else if (m.type === 'code') {
                parts.push(<code key={key++} className="bg-slate-700/50 text-pink-400 px-1.5 py-0.5 rounded text-sm font-mono">{m.content}</code>);
            }
            lastIndex = m.end;
        }
    });

    if (lastIndex < text.length) {
        parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : text;
};

// Quick reaction emojis (like Slack/Rocket.Chat)
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

const TeamChat = ({ userEmail, initialConversations = [], isPreloaded = false, onPreview, subscription, onUpgradePlan, onUnreadCountChange }) => {
    // isEmbedded is imported from App.js for consistent detection across all components

    // Use preloaded conversations for instant display
    const [conversations, setConversations] = useState(initialConversations);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState("");
    const [isLoading, setIsLoading] = useState(!isPreloaded);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [isNewChatOpen, setIsNewChatOpen] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [isLoadingTeam, setIsLoadingTeam] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [groupChatName, setGroupChatName] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [notificationPermission, setNotificationPermission] = useState("default");
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [loadError, setLoadError] = useState(null);

    // Delete conversation state
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // New Rocket.Chat-inspired features state
    const [messageActionsMenu, setMessageActionsMenu] = useState(null); // { messageId, position }
    const [editingMessage, setEditingMessage] = useState(null); // message being edited
    const [editText, setEditText] = useState('');
    const [replyingTo, setReplyingTo] = useState(null); // message being replied to
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [starredMessages, setStarredMessages] = useState([]);
    const [showPinnedPanel, setShowPinnedPanel] = useState(false);
    const [showMembersPanel, setShowMembersPanel] = useState(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [isRemovingMember, setIsRemovingMember] = useState(null); // email of member being removed
    const [memberToRemove, setMemberToRemove] = useState(null); // member object for confirmation modal
    const [messageToDelete, setMessageToDelete] = useState(null);
    const [messageSearchTerm, setMessageSearchTerm] = useState('');
    const [showMessageSearch, setShowMessageSearch] = useState(false);
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [showMentionPopup, setShowMentionPopup] = useState(false);
    const [mentionCursorPos, setMentionCursorPos] = useState(0);
    const [showFormattingBar, setShowFormattingBar] = useState(false);
    const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null);
    const [showQuickReactions, setShowQuickReactions] = useState(null); // messageId for quick reactions
    const [deletedMessageIds, setDeletedMessageIds] = useState(new Set()); // Track locally deleted messages to prevent re-fetch
    const deletedMessageIdsRef = useRef(new Set()); // Ref for use in polling without causing re-renders
    const globalDeletedMessageIdsRef = useRef(new Set()); // Track ALL deleted messages globally (persists across conversation switches)
    const locallyModifiedConvsRef = useRef(new Set()); // Track conversations with locally modified last_message
    const [forwardingMessageId, setForwardingMessageId] = useState(null); // Prevent double-forward
    const [showJumpToBottom, setShowJumpToBottom] = useState(false); // Slack-style jump to bottom button
    const [lastReadTimestamp, setLastReadTimestamp] = useState(null); // For unread divider

    const tier = normalizeTier(subscription?.plan?.name || subscription?.tier);
    const isFreeUser = tier === 'free';

    // File attachments state
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Voice/Video recording state
    const [isRecordingAudio, setIsRecordingAudio] = useState(false);
    const [isRecordingVideo, setIsRecordingVideo] = useState(false);

    // Link preview state
    const [pendingPreviews, setPendingPreviews] = useState([]);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const previousConversationId = useRef(null);
    const activeConversationIdRef = useRef(activeConversationId);
    const previousConversations = useRef([]);
    const notificationAudioRef = useRef(null);
    // Note: chatAuthErrorOccurred is a module-level variable to persist across StrictMode remounts

    // Keep ref updated
    useEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);

    // Request notification permission on mount
    useEffect(() => {
        if ('Notification' in window) {
            setNotificationPermission(Notification.permission);

            if (Notification.permission === 'default') {
                // Request permission after a short delay to not be intrusive on initial load
                setTimeout(() => {
                    Notification.requestPermission().then(permission => {
                        setNotificationPermission(permission);
                    });
                }, 3000);
            }
        }

        // Create notification audio element
        notificationAudioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIHmW86+OUWAwMUKbf8bllHAU2jdXyz3kpBS16yO/eizQIKmvE7eCcUg0LSp3e8rlpHAQygM/y2oYmBieB0PLXhzMJKnDA7uCOTw4NTKTg87JnHQU0jNTy0HkqBSx4xu7gjkwOD0yj3fK0aB0FMoHP8taGKAYnf9Hx1YY0CStu0O3blGUQEEqb2/G8biMGMIHO8tnIBQEt'); // Notification sound
    }, []);

    // Detect new messages and show notifications
    useEffect(() => {
        if (!conversations || conversations.length === 0) return;

        // Skip on initial load
        if (previousConversations.current.length === 0) {
            previousConversations.current = conversations;
            return;
        }

        // Check for new messages in conversations
        conversations.forEach(conv => {
            const prevConv = previousConversations.current.find(c => c.id === conv.id);

            // New message detected if last_message changed or unread count increased
            const hasNewMessage = prevConv && (
                (conv.last_message?.id && conv.last_message?.id !== prevConv.last_message?.id) ||
                (conv.unread_count > 0 && conv.unread_count > (prevConv.unread_count || 0))
            );

            if (hasNewMessage) {
                const lastMessage = conv.last_message;

                // Don't notify for own messages
                if (lastMessage?.sender_email === userEmail) return;

                // Don't notify if conversation is currently active and window is focused
                if (conv.id === activeConversationId && !document.hidden) return;

                // Show notification
                showNotification(conv, lastMessage);
            }
        });

        // Update previous state
        previousConversations.current = conversations;
    }, [conversations, activeConversationId, userEmail]);

    // Show browser notification
    const showNotification = (conversation, message) => {
        // Play sound
        if (notificationAudioRef.current) {
            notificationAudioRef.current.play().catch(err => {
            });
        }

        // Show browser notification if permission granted
        if (notificationPermission === 'granted' && 'Notification' in window) {
            const senderName = message.sender_name || message.sender_email?.split('@')[0] || 'Someone';
            const conversationName = conversation.name || conversation.participants
                .map(p => p.name || p.email.split('@')[0])
                .join(', ');

            const messagePreview = message.text?.substring(0, 100) || 'New message';
            const hasAttachments = message.attachments && message.attachments.length > 0;
            const body = hasAttachments
                ? `${messagePreview}\n📎 ${message.attachments.length} attachment(s)`
                : messagePreview;

            const notification = new Notification(`${senderName} in ${conversationName}`, {
                body: body,
                icon: '/logo192.png', // You can customize this
                tag: conversation.id, // Prevent duplicate notifications for same conversation
                requireInteraction: false,
                silent: true // We already played our own sound
            });

            // Click notification to open conversation
            notification.onclick = () => {
                // Skip window.focus() when embedded to prevent scroll issues
                if (!isEmbedded) {
                    window.focus();
                }
                setActiveConversationId(conversation.id);
                notification.close();
            };

            // Auto-close after 5 seconds
            setTimeout(() => notification.close(), 5000);
        }
    };

    // Handle notification permission toggle
    const handleNotificationToggle = async () => {
        if (!('Notification' in window)) {
            alert('Your browser does not support notifications');
            return;
        }

        if (notificationPermission === 'denied') {
            alert('Notifications are blocked. Please enable them in your browser settings.');
            return;
        }

        if (notificationPermission === 'default' || notificationPermission === 'granted') {
            const permission = await Notification.requestPermission();
            setNotificationPermission(permission);

            if (permission === 'granted') {
                // Show a test notification
                const testNotification = new Notification('Notifications Enabled', {
                    body: 'You will now receive message notifications',
                    icon: '/logo192.png',
                    silent: true
                });
                setTimeout(() => testNotification.close(), 3000);
            }
        }
    };

    // Track if we've already initialized with preloaded data
    const hasInitializedPreload = useRef(false);

    // Update conversations when preloaded data arrives (only once)
    useEffect(() => {
        if (isPreloaded && initialConversations.length > 0 && !hasInitializedPreload.current) {
            hasInitializedPreload.current = true;
            setConversations(initialConversations);
            setIsLoading(false);
            // Auto-select first conversation if none selected
            if (!activeConversationId) {
                setActiveConversationId(initialConversations[0].id);
            }
        }
    }, [isPreloaded, initialConversations, activeConversationId]);

    // Update page title with unread count and notify parent
    useEffect(() => {
        const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

        if (totalUnread > 0) {
            document.title = `(${totalUnread}) Team Chat - CloudMortgage.io`;
        } else {
            document.title = 'Team Chat - CloudMortgage.io';
        }

        // Notify parent of unread count change
        if (onUnreadCountChange) {
            onUnreadCountChange(totalUnread);
        }

        // Cleanup on unmount
        return () => {
            document.title = 'CloudMortgage.io';
        };
    }, [conversations, onUnreadCountChange]);

    // Load team members when modal opens
    useEffect(() => {
        if (isNewChatOpen) {
            loadTeamMembers();
        }
    }, [isNewChatOpen]);

    const loadTeamMembers = async () => {
        setIsLoadingTeam(true);
        try {
            const response = await fetchTeamMembers();
            const members = response?.data?.members || response?.data || [];
            const filteredMembers = members.filter(m => m.email !== userEmail);
            setTeamMembers(filteredMembers);
        } catch (error) {
            console.error("Failed to load team members:", error);
            setTeamMembers([]);
        } finally {
            setIsLoadingTeam(false);
        }
    };

    const toggleMemberSelection = (member) => {
        setSelectedMembers(prev => {
            const isSelected = prev.some(m => m.email === member.email);
            if (isSelected) {
                return prev.filter(m => m.email !== member.email);
            } else {
                return [...prev, member];
            }
        });
    };

    // Add a member to the current group conversation
    const handleAddMemberToGroup = async (memberEmail) => {
        if (!activeConversation || !memberEmail) return;

        setIsAddingMember(true);
        try {
            await ChatService.addParticipant(activeConversation.id, memberEmail);

            // Update the conversation participants locally
            const memberToAdd = teamMembers.find(m => m.email === memberEmail);
            setConversations(prev =>
                prev.map(c => c.id === activeConversation.id
                    ? {
                        ...c,
                        participants: [
                            ...c.participants,
                            memberToAdd || { email: memberEmail, name: memberEmail.split('@')[0] }
                        ]
                    }
                    : c
                )
            );

            setShowAddMemberModal(false);
        } catch (error) {
            console.error("Failed to add member:", error);
            alert(error.message || "Failed to add member to conversation");
        } finally {
            setIsAddingMember(false);
        }
    };

    // Remove a member from the current group conversation
    const handleRemoveMemberFromGroup = async (memberEmail) => {
        if (!activeConversation || !memberEmail) return;

        const isLeavingGroup = memberEmail === userEmail;

        setIsRemovingMember(memberEmail);
        try {
            await ChatService.removeParticipant(activeConversation.id, memberEmail);

            if (isLeavingGroup) {
                // User left the group - remove conversation from list and clear selection
                setConversations(prev => prev.filter(c => c.id !== activeConversation.id));
                setActiveConversationId(null);
                setMessages([]);
            } else {
                // Remove the member from conversation participants locally
                setConversations(prev =>
                    prev.map(c => c.id === activeConversation.id
                        ? {
                            ...c,
                            participants: c.participants.filter(p => p.email !== memberEmail)
                        }
                        : c
                    )
                );
            }

            setMemberToRemove(null);
            setShowMembersPanel(false);
        } catch (error) {
            console.error("Failed to remove member:", error);
            alert(error.message || "Failed to remove member from conversation");
        } finally {
            setIsRemovingMember(null);
        }
    };

    const handleConversationSelect = async (conv) => {
        setActiveConversationId(conv.id);

        if (conv.unread_count > 0) {
            // Optimistic update
            setConversations(prev =>
                prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c)
            );

            // Backend call
            try {
                await ChatService.markAsRead(conv.id);
            } catch (error) {
                console.error("Failed to mark chat as read:", error);
            }
        }
    };

    const handleStartChat = async () => {
        if (selectedMembers.length === 0) return;

        try {
            const participantEmails = selectedMembers.map(m => m.email).filter(Boolean);

            if (participantEmails.length === 0) {
                alert("Please select at least one team member");
                return;
            }

            const payload = {
                participants: participantEmails,
                // Send name/topic only for group chats (2+ participants)
                // Backend enforces this rule: names only allowed for groups
                ...(groupChatName.trim() && selectedMembers.length > 1 && { name: groupChatName.trim() })
            };

            const response = await ChatService.startConversation(payload);
            if (response.data) {
                setActiveConversationId(response.data.id);
                setIsNewChatOpen(false);
                setSelectedMembers([]);
                setGroupChatName("");
                const convResponse = await ChatService.getConversations({ skipGlobalAuthHandler: true });
                if (convResponse.data) {
                    setConversations(convResponse.data);
                }
            }
        } catch (error) {
            console.error("❌ Failed to start conversation:", error);
            alert(`Failed to create chat: ${error.message}\n\nCheck the console for details.`);
        }
    };

    // Handle delete conversation
    const handleDeleteConversation = async () => {
        if (!conversationToDelete) return;

        // Handle both id and _id fields (MongoDB uses _id)
        const convId = conversationToDelete.id || conversationToDelete._id;

        if (!convId) {
            alert("Cannot delete conversation: No valid ID found");
            return;
        }

        setIsDeleting(true);
        try {
            await ChatService.deleteConversation(convId);

            // Remove from local state (handle both id and _id)
            setConversations(prev => prev.filter(c => (c.id || c._id) !== convId));

            // Clear active conversation if it was the deleted one
            if (activeConversationId === convId) {
                setActiveConversationId(null);
                setMessages([]);
            }

            // Close modal
            setConversationToDelete(null);
        } catch (error) {
            console.error("❌ Failed to delete conversation:", error);
            alert(`Failed to delete conversation: ${error.message}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // Global polling for conversation list
    useEffect(() => {
        let pollingInterval = null;

        const fetchConversations = async () => {
            // Don't poll if auth error already occurred
            if (chatAuthErrorOccurred) return;

            try {
                // Use skipGlobalAuthHandler to prevent cascade logouts on 401 errors
                const response = await ChatService.getConversations({ skipGlobalAuthHandler: true });

                if (response.data) {
                    // Normalize conversations data
                    const normalizedConversations = Array.isArray(response.data)
                        ? response.data
                        : Array.isArray(response.data.conversations)
                            ? response.data.conversations
                            : [];

                    // Sort conversations by last activity (most recent first)
                    const sortedConversations = [...normalizedConversations].sort((a, b) => {
                        const aTime = a.last_message_at || a.lastMessageAt || a.updated_at || a.created_at || 0;
                        const bTime = b.last_message_at || b.lastMessageAt || b.updated_at || b.created_at || 0;
                        return new Date(bTime) - new Date(aTime); // Descending (newest first)
                    });

                    // Smart update: only update if there are actual changes to prevent unnecessary re-renders
                    setConversations(prev => {
                        // If this is the first load, just set the conversations
                        if (prev.length === 0) return sortedConversations;

                        // Check if there are any actual changes (new conversations, updated timestamps, etc.)
                        const prevIds = new Set(prev.map(c => c.id));
                        const newIds = new Set(sortedConversations.map(c => c.id));

                        const hasNewConversation = sortedConversations.some(c => !prevIds.has(c.id));
                        const hasRemovedConversation = prev.some(c => !newIds.has(c.id));

                        // Check if any conversation has updated last_message from the server
                        const hasUpdatedMessage = sortedConversations.some(newConv => {
                            const oldConv = prev.find(c => c.id === newConv.id);
                            if (!oldConv) return false;
                            const oldMsgTime = oldConv.last_message_at || oldConv.lastMessageAt || oldConv.updated_at || '';
                            const newMsgTime = newConv.last_message_at || newConv.lastMessageAt || newConv.updated_at || '';
                            return newMsgTime > oldMsgTime; // Only if server has newer data
                        });

                        // Only update if there are actual changes
                        if (hasNewConversation || hasRemovedConversation || hasUpdatedMessage) {
                            // Merge: preserve local last_message for locally modified conversations
                            return sortedConversations.map(serverConv => {
                                const localConv = prev.find(c => c.id === serverConv.id);
                                if (!localConv) return serverConv;

                                // If this conversation was locally modified (message deleted/sent)
                                if (locallyModifiedConvsRef.current.has(serverConv.id)) {
                                    const localMsgTime = localConv.last_message_at || localConv.lastMessageAt || '';
                                    const serverMsgTime = serverConv.last_message_at || serverConv.lastMessageAt || '';
                                    const serverMsgId = serverConv.last_message?.id;
                                    const localMsgId = localConv.last_message?.id;

                                    // Never accept server's last_message if it's a deleted message
                                    if (serverMsgId && globalDeletedMessageIdsRef.current.has(serverMsgId)) {
                                        // Server is returning a deleted message, keep local
                                        return {
                                            ...serverConv,
                                            last_message: localConv.last_message,
                                            last_message_at: localConv.last_message_at || localConv.lastMessageAt
                                        };
                                    }

                                    // Accept server data if it has a genuinely NEW message (different ID and newer)
                                    if (serverMsgId && serverMsgId !== localMsgId && serverMsgTime && localMsgTime && new Date(serverMsgTime) > new Date(localMsgTime)) {
                                        // Server has newer message, clear the modification flag and use server data
                                        locallyModifiedConvsRef.current.delete(serverConv.id);
                                        return serverConv;
                                    }

                                    // Otherwise keep local last_message
                                    return {
                                        ...serverConv,
                                        last_message: localConv.last_message,
                                        last_message_at: localConv.last_message_at || localConv.lastMessageAt
                                    };
                                }

                                // Even if not locally modified, check if server is returning a deleted message
                                if (serverConv.last_message?.id && globalDeletedMessageIdsRef.current.has(serverConv.last_message.id)) {
                                    // Server is returning a deleted message, keep local if available
                                    if (localConv.last_message) {
                                        return {
                                            ...serverConv,
                                            last_message: localConv.last_message,
                                            last_message_at: localConv.last_message_at || localConv.lastMessageAt
                                        };
                                    }
                                }

                                // Otherwise use server data
                                return serverConv;
                            });
                        }

                        // No changes, keep existing order to prevent jumps
                        return prev;
                    });
                    // Only auto-select if we don't have preloaded data
                    if (sortedConversations.length > 0 && !activeConversationId && !isPreloaded) {
                        setActiveConversationId(sortedConversations[0].id);
                    }
                }
            } catch (error) {
                // Check if this is an auth error - if so, show specific message and stop polling
                const isAuthError = error.message?.includes("Authentication expired") ||
                                   error.message?.includes("sign in");
                if (isAuthError) {
                    // Don't log auth errors - they're expected when backend rejects token
                    setLoadError("Session expired. Please refresh the page to sign in again.");
                    // Stop polling on auth errors to prevent spam (use ref to persist across re-renders)
                    chatAuthErrorOccurred = true;
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        pollingInterval = null;
                    }
                } else {
                    console.error("❌ Failed to load conversations:", error);
                    setLoadError(error.message || "Failed to load conversations");
                }
            } finally {
                setIsLoading(false);
            }
        };

        // Skip initial fetch if we have preloaded data OR if auth error already occurred
        if (!isPreloaded && !chatAuthErrorOccurred) {
            fetchConversations();
        }

        // Optimize polling: only poll when tab is active
        const handleVisibilityChange = () => {
            if (chatAuthErrorOccurred) return; // Don't resume polling if auth error occurred
            if (document.hidden) {
                // Stop polling when tab is not visible
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }
            } else {
                // Resume polling when tab becomes visible
                fetchConversations();
                pollingInterval = setInterval(fetchConversations, 5000);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Only start polling if no auth error has occurred
        if (!chatAuthErrorOccurred) {
            pollingInterval = setInterval(fetchConversations, 5000);
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeConversationId, isPreloaded]);

    // Load messages when conversation changes
    useEffect(() => {
        let interval;
        if (activeConversationId) {
            // Show loading state and CLEAR OLD MESSAGES when switching conversations
            if (previousConversationId.current !== activeConversationId) {
                setIsLoadingMessages(true);
                setMessages([]); // Clear old messages immediately to prevent flashing
                // Clear deleted message tracking for new conversation
                setDeletedMessageIds(new Set());
                deletedMessageIdsRef.current = new Set();
                previousConversationId.current = activeConversationId;
            }

            const fetchMessages = async () => {
                try {
                    // Store which conversation we're fetching for
                    const fetchingForConversationId = activeConversationId;

                    const response = await ChatService.getMessages(fetchingForConversationId, { limit: 100 });

                    // Only update if we're still on the same conversation
                    // (user might have switched to another conversation while fetching)
                    if (fetchingForConversationId !== activeConversationIdRef.current) {
                        return;
                    }

                    if (response.data) {
                        // Track if there might be more messages (if we got the full limit)
                        setHasMoreMessages(response.data.length >= 100);

                        // Optimize: Only sort and process if data exists
                        if (response.data.length === 0) {
                            setMessages([]);
                            setHasMoreMessages(false);
                            setIsLoadingMessages(false);
                            return;
                        }

                        // Sort messages chronologically (oldest first, newest last)
                        const sortedMessages = [...response.data].sort((a, b) => {
                            return new Date(a.created_at) - new Date(b.created_at);
                        });

                        // Create a lookup map for populating reply_to from reply_to_id
                        const messageMap = new Map();
                        sortedMessages.forEach(msg => {
                            messageMap.set(msg.id, msg);
                        });

                        // Process reactions from backend
                        const messagesWithReactions = sortedMessages
                            // Filter out locally deleted messages (prevents re-fetch from bringing them back)
                            .filter(msg => !deletedMessageIdsRef.current.has(msg.id))
                            .map(msg => {
                                // Normalize field names - API might use different field names
                                const normalizedMsg = {
                                    ...msg,
                                    text: msg.text || msg.message || msg.content || msg.body || '',
                                    sender_email: msg.sender_email || msg.senderEmail || msg.from_email || msg.from || '',
                                    sender_name: msg.sender_name || msg.senderName || msg.from_name || '',
                                    reactions: msg.reactions || [],
                                    attachments: Array.isArray(msg.attachments)
                                        ? msg.attachments.map(normalizeAttachment)
                                        : []
                                };

                                // Populate reply_to from reply_to_id if not already populated
                                if (!normalizedMsg.reply_to && (msg.reply_to_id || msg.replyToId)) {
                                    const replyToId = msg.reply_to_id || msg.replyToId;
                                    const referencedMsg = messageMap.get(replyToId);
                                    if (referencedMsg) {
                                        normalizedMsg.reply_to = {
                                            id: replyToId,
                                            text: referencedMsg.text || referencedMsg.message || referencedMsg.content || referencedMsg.body || '',
                                            sender_name: referencedMsg.sender_name || referencedMsg.senderName || referencedMsg.from_name ||
                                                (referencedMsg.sender_email || referencedMsg.senderEmail || referencedMsg.from_email || referencedMsg.from || '').split('@')[0]
                                        };
                                    }
                                }

                                return normalizedMsg;
                            });

                        // Smart update: preserve temp messages and only update if there are actual changes
                        setMessages(prevMessages => {
                            // Keep any temp/sending messages
                            const tempMessages = prevMessages.filter(msg => msg.isTemp && msg.status === 'sending');

                            // Find new messages (messages not in prevMessages)
                            const prevMessageIds = new Set(prevMessages.filter(m => !m.isTemp).map(m => m.id));
                            const hasNewMessages = messagesWithReactions.some(msg => !prevMessageIds.has(msg.id));

                            // Only update if there are new messages or this is initial load
                            if (hasNewMessages || prevMessages.length === 0 || prevMessages.every(m => m.isTemp)) {
                                // Merge and sort to ensure correct order
                                const merged = [...messagesWithReactions, ...tempMessages];
                                return merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                            }

                            // Otherwise, just update existing messages (for reaction updates)
                            const updatedMessages = prevMessages.map(prevMsg => {
                                if (prevMsg.isTemp) return prevMsg;
                                const serverMsg = messagesWithReactions.find(m => m.id === prevMsg.id);
                                return serverMsg || prevMsg;
                            });

                            // Always ensure sorted order
                            return updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                        });

                        // Always sync conversation's last_message with the actual latest message
                        // This ensures the preview matches reality (handles deleted messages, etc.)
                        if (messagesWithReactions.length > 0) {
                            const latestMsg = messagesWithReactions[messagesWithReactions.length - 1];

                            setConversations(prevConvs => prevConvs.map(conv => {
                                if (conv.id === activeConversationId) {
                                    // Always sync with actual latest message from messages list
                                    const currentLastMsgId = conv.last_message?.id;
                                    const actualLastMsgId = latestMsg.id;

                                    // Update if different (covers deleted messages, new messages, etc.)
                                    if (currentLastMsgId !== actualLastMsgId) {
                                        // Mark as locally modified to preserve this update
                                        locallyModifiedConvsRef.current.add(activeConversationId);
                                        return {
                                            ...conv,
                                            last_message: {
                                                id: latestMsg.id,
                                                text: latestMsg.text || latestMsg.message || latestMsg.content || '',
                                                sender_email: latestMsg.sender_email,
                                                created_at: latestMsg.created_at
                                            },
                                            last_message_at: latestMsg.created_at
                                        };
                                    }
                                }
                                return conv;
                            }));
                        }

                        setIsLoadingMessages(false);
                    }
                } catch (error) {
                    console.error("❌ Failed to load messages:", error);
                    setIsLoadingMessages(false);
                    setLoadError(`Failed to load messages: ${error.message}`);
                }
            };

            fetchMessages();

            // Optimize polling: only poll when tab is active and conversation is active
            const handleVisibilityChange = () => {
                if (document.hidden) {
                    if (interval) clearInterval(interval);
                } else {
                    fetchMessages();
                    interval = setInterval(fetchMessages, 3000);
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            interval = setInterval(fetchMessages, 3000);

            return () => {
                clearInterval(interval);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [activeConversationId]);

    // Load older messages (pagination)
    const loadOlderMessages = useCallback(async () => {
        if (!activeConversationId || isLoadingOlderMessages || !hasMoreMessages || messages.length === 0) return;

        setIsLoadingOlderMessages(true);

        try {
            // Get the oldest message's ID or created_at to use as cursor
            const oldestMessage = messages[0];
            const beforeId = oldestMessage?.id;

            if (!beforeId) {
                setIsLoadingOlderMessages(false);
                return;
            }

            const response = await ChatService.getMessages(activeConversationId, {
                limit: 50,
                before: beforeId
            });

            if (response.data && response.data.length > 0) {
                // Sort older messages chronologically
                const sortedOlder = [...response.data].sort((a, b) =>
                    new Date(a.created_at) - new Date(b.created_at)
                );

                // Normalize the older messages
                const normalizedOlder = sortedOlder
                    .filter(msg => !deletedMessageIdsRef.current.has(msg.id))
                    .map(msg => ({
                        ...msg,
                        text: msg.text || msg.message || msg.content || msg.body || '',
                        sender_email: msg.sender_email || msg.senderEmail || msg.from_email || msg.from || '',
                        sender_name: msg.sender_name || msg.senderName || msg.from_name || '',
                        reactions: msg.reactions || [],
                        attachments: Array.isArray(msg.attachments) ? msg.attachments : []
                    }));

                // Prepend older messages to existing messages
                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const newOlder = normalizedOlder.filter(m => !existingIds.has(m.id));
                    return [...newOlder, ...prev];
                });

                // Check if there might be even more messages
                setHasMoreMessages(response.data.length >= 50);
            } else {
                setHasMoreMessages(false);
            }
        } catch (error) {
            console.error("Failed to load older messages:", error);
        } finally {
            setIsLoadingOlderMessages(false);
        }
    }, [activeConversationId, isLoadingOlderMessages, hasMoreMessages, messages]);

    // Smooth scroll to bottom on new messages
    // When embedded in iframe, completely disable auto-scroll to prevent parent page scroll
    useEffect(() => {
        if (isEmbedded) return; // Skip auto-scroll entirely when embedded
        if (messagesEndRef.current && !isLoadingMessages && !showScrollButton) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoadingMessages, showScrollButton, isEmbedded]);

    // Detect scroll position to show scroll-to-bottom button
    const handleScroll = useCallback((e) => {
        const container = e.target;
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        setShowScrollButton(!isNearBottom && messages.length > 5);
    }, [messages.length]);

    // Scroll to bottom function (user-initiated, so allow even when embedded)
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            // Use container scrollTop instead of scrollIntoView to avoid parent scroll
            const container = messagesContainerRef.current;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }
        setShowScrollButton(false);
    };

    // Auto-focus input when conversation changes
    // When embedded, skip auto-focus entirely to prevent any scroll issues
    useEffect(() => {
        if (isEmbedded) return;
        if (activeConversationId && inputRef.current && !isLoadingMessages) {
            inputRef.current.focus();
        }
    }, [activeConversationId, isLoadingMessages, isEmbedded]);

    // Detect URLs in message input
    useEffect(() => {
        const detectUrls = async () => {
            const urlPattern = /https?:\/\/[^\s]+/g;
            const urls = messageInput.match(urlPattern);

            if (!urls || urls.length === 0) {
                return;
            }

            // Only generate previews for new URLs
            const newUrls = urls.filter(url =>
                !pendingPreviews.some(p => p.url === url)
            );

            if (newUrls.length === 0) return;

            setIsGeneratingPreview(true);

            try {
                // Generate previews for new URLs (limit to 3)
                const previewPromises = newUrls.slice(0, 3).map(url =>
                    ChatService.generateLinkPreview(url).catch(err => {
                        console.warn(`Failed to generate preview for ${url}:`, err);
                        return null;
                    })
                );

                const results = await Promise.all(previewPromises);
                const validPreviews = results
                    .filter(r => r !== null && r.data)
                    .map(r => r.data);

                if (validPreviews.length > 0) {
                    setPendingPreviews(prev => {
                        // Avoid duplicates
                        const existingUrls = new Set(prev.map(p => p.url));
                        const uniqueNew = validPreviews.filter(p => !existingUrls.has(p.url));
                        return [...prev, ...uniqueNew];
                    });
                }
            } catch (error) {
                console.error("Preview generation error:", error);
            } finally {
                setIsGeneratingPreview(false);
            }
        };

        // Debounce URL detection
        const timeoutId = setTimeout(detectUrls, 500);
        return () => clearTimeout(timeoutId);
    }, [messageInput, pendingPreviews]);

    const getFilenameFromKey = (key) => {
        if (!key || typeof key !== "string") return "";
        const parts = key.split("/");
        const last = parts[parts.length - 1];
        return last || "";
    };

    const getFilenameFromUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        try {
            const parsed = new URL(url);
            return getFilenameFromKey(parsed.pathname);
        } catch (error) {
            return getFilenameFromKey(url);
        }
    };

    const normalizeAttachment = (attachment) => {
        if (!attachment) return attachment;
        if (typeof attachment === "string") {
            if (attachment.includes("/")) {
                return {
                    id: attachment,
                    objectKey: attachment,
                    filename: getFilenameFromKey(attachment) || "Attachment"
                };
            }
            return { id: attachment, filename: "Attachment" };
        }

        const objectKey = attachment.objectKey || attachment.object_key || attachment.s3_key;
        const filename = attachment.filename || attachment.name || attachment.fileName || attachment.originalName || attachment.original_filename || attachment.file_name || attachment.original_name;
        const fileType = attachment.file_type || attachment.type || attachment.contentType;
        const resolvedFilename = filename
            || getFilenameFromKey(objectKey)
            || getFilenameFromUrl(attachment.signed_url || attachment.url || attachment.signedUrl || attachment.previewUrl || attachment.downloadUrl);

        const normalized = {
            ...attachment,
            objectKey,
            filename: attachment.filename || resolvedFilename,
            file_type: attachment.file_type || fileType
        };

        return normalized;
    };


    const getAttachmentName = (attachment) => {
        if (!attachment) return "Attachment";
        if (typeof attachment === "string") return attachment;

        return attachment?.filename ||
            attachment?.name ||
            attachment?.fileName ||
            attachment?.originalName ||
            attachment?.original_filename ||
            attachment?.file_name ||
            attachment?.original_name ||
            getFilenameFromKey(attachment?.objectKey || attachment?.object_key || attachment?.s3_key) ||
            getFilenameFromUrl(getAttachmentUrl(attachment)) ||
            "Attachment";
    };

    const getAttachmentType = (attachment) =>
        attachment?.file_type ||
        attachment?.type ||
        attachment?.contentType ||
        "";

    const getAttachmentUrl = (attachment) =>
        attachment?.signed_url ||
        attachment?.url ||
        attachment?.signedUrl ||
        attachment?.previewUrl ||
        attachment?.downloadUrl ||
        "";

    const handleAttachmentPreview = async (attachment) => {
        const normalized = normalizeAttachment(attachment);
        const objectKey = normalized?.objectKey || normalized?.object_key || normalized?.s3_key;
        let link = null;

        // First try to use any existing URL from the attachment
        link = getAttachmentUrl(normalized);

        // If no URL exists, try to get a signed URL from the API
        if (!link && objectKey) {
            try {
                const response = await ChatService.getAttachmentSignedUrl(objectKey);
                link = response?.url || response?.data?.url || response?.data?.signedUrl;
            } catch (error) {
                console.error("❌ Failed to fetch signed URL:", error);
            }
        }

        if (!link) {
            console.warn("Preview not available - signed URL could not be retrieved for:", objectKey);
            return;
        }

        const updatedAttachment = {
            ...normalized,
            signed_url: link,
            previewUrl: link,
            url: link
        };

        setMessages((prev) =>
            prev.map((msg) => ({
                ...msg,
                attachments: Array.isArray(msg.attachments)
                    ? msg.attachments.map((att) => {
                        const candidate = normalizeAttachment(att);
                        if (candidate?.id && updatedAttachment.id && candidate.id === updatedAttachment.id) {
                            return updatedAttachment;
                        }
                        if (candidate?.objectKey && updatedAttachment.objectKey && candidate.objectKey === updatedAttachment.objectKey) {
                            return updatedAttachment;
                        }
                        if (candidate?.s3_key && updatedAttachment.s3_key && candidate.s3_key === updatedAttachment.s3_key) {
                            return updatedAttachment;
                        }
                        return att;
                    })
                    : msg.attachments
            }))
        );

        if (onPreview) {
            const previewData = {
                ...updatedAttachment,
                name: updatedAttachment.filename || updatedAttachment.name || "Attachment",
                url: link,
                type: updatedAttachment.file_type || updatedAttachment.type || updatedAttachment.contentType || "application/octet-stream"
            };
            onPreview(previewData);
            return;
        }

        window.open(link, "_blank");
    };

    const MAX_FILE_SIZE_MB = 50;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const uploadFiles = async (files) => {
        if (!files || files.length === 0) return;

        // Client-side file size validation
        const oversizedFiles = files.filter(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)}MB)`).join(', ');
            alert(`File(s) too large (max ${MAX_FILE_SIZE_MB}MB): ${fileNames}`);
            return;
        }

        setIsUploading(true);

        try {
            // Upload files in parallel
            const uploadPromises = files.map(file =>
                ChatService.uploadAttachment(file)
            );

            const results = await Promise.all(uploadPromises);
            const newAttachments = results.map(r => normalizeAttachment(r.data || r));

            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (error) {
            console.error("File upload failed:", error);
            // Extract meaningful error message from server response
            let errorMsg = "Failed to upload one or more files.";
            if (error.message) {
                const sizeMatch = error.message.match(/File size.*exceeds.*maximum/i);
                if (sizeMatch) {
                    errorMsg = `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`;
                } else if (error.message.includes("Failed to upload")) {
                    errorMsg = error.message;
                }
            }
            alert(errorMsg);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        await uploadFiles(files);
    };

    // Voice recording complete handler
    const handleVoiceRecordingComplete = async (result) => {
        setIsRecordingAudio(false);
        if (result && result.file) {
            await uploadFiles([result.file]);
        }
    };

    // Video recording complete handler
    const handleVideoRecordingComplete = async (result) => {
        setIsRecordingVideo(false);
        if (result && result.file) {
            await uploadFiles([result.file]);
        }
    };

    const handlePaste = async (event) => {
        const items = event.clipboardData?.items || [];
        const files = [];

        Array.from(items).forEach((item) => {
            if (item.kind === "file") {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        });

        if (files.length > 0) {
            event.preventDefault();
            await uploadFiles(files);
        }
    };

    const removeAttachment = (attachmentId) => {
        setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    };

    const removePendingPreview = (url) => {
        setPendingPreviews(prev => prev.filter(p => p.url !== url));
    };

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();

        // Allow sending if there's text OR attachments
        if ((!messageInput.trim() && attachments.length === 0) || !activeConversationId || isSending) return;

        const text = messageInput.trim();
        // Grab values before resetting state
        const currentAttachments = [...attachments];
        const currentPreviews = [...pendingPreviews];
        const currentReplyTo = replyingTo;

        const tempId = `temp-${Date.now()}`;

        // Reset input state immediately for better UX
        setMessageInput("");
        setAttachments([]);
        setPendingPreviews([]);
        setShowEmojiPicker(false);
        setReplyingTo(null);
        setShowMentionPopup(false);
        setIsSending(true);

        // Optimistic update with temp message
        const tempMessage = {
            id: tempId,
            text,
            sender_email: userEmail,
            sender_name: userEmail.split('@')[0],
            created_at: new Date().toISOString(),
            isTemp: true,
            status: 'sending',
            reactions: [],
            attachments: currentAttachments,
            link_previews: currentPreviews,
            reply_to_id: currentReplyTo?.id || null,
            reply_to: currentReplyTo ? {
                id: currentReplyTo.id,
                text: currentReplyTo.text || currentReplyTo.message || currentReplyTo.content,
                sender_name: currentReplyTo.sender_name || currentReplyTo.sender_email?.split('@')[0]
            } : null
        };

        setMessages(prev => [...prev, tempMessage]);

        try {
            const replyToId = currentReplyTo?.id || null;
            // Pass full attachment objects (not just IDs) - backend requires id, filename, file_type, s3_key
            const response = await ChatService.sendMessage(activeConversationId, text, currentAttachments, currentPreviews, replyToId);

            const returnedMsg = response.data;

            // Check if backend returned safe attachments (objects) or just IDs (strings)
            const backendAttachments = returnedMsg.attachments || [];
            const normalizedBackendAttachments = Array.isArray(backendAttachments)
                ? backendAttachments.map(normalizeAttachment).filter(Boolean)
                : [];
            const hasValidBackendAttachments = normalizedBackendAttachments.length > 0
                && normalizedBackendAttachments.every(att =>
                    typeof att === "object"
                    && (att.objectKey || att.object_key || att.s3_key || getAttachmentUrl(att))
                );

            const finalMessage = {
                ...returnedMsg,
                // Ensure sender_email matches to keep "isMe" valid
                sender_email: returnedMsg.sender_email || userEmail,
                // Persist attachments: Use backend's only if they are full objects, otherwise keep ours
                attachments: hasValidBackendAttachments ? normalizedBackendAttachments : currentAttachments,
                // Persist previews if backend didn't return them
                link_previews: (returnedMsg.link_previews && returnedMsg.link_previews.length > 0)
                    ? returnedMsg.link_previews
                    : currentPreviews,
                // Persist reply_to if backend didn't return it (backend may return reply_to_id instead)
                reply_to: returnedMsg.reply_to || (currentReplyTo ? {
                    id: currentReplyTo.id,
                    text: currentReplyTo.text || currentReplyTo.message || currentReplyTo.content,
                    sender_name: currentReplyTo.sender_name || currentReplyTo.sender_email?.split('@')[0]
                } : null),
                // Also track reply_to_id for future fetches
                reply_to_id: returnedMsg.reply_to_id || currentReplyTo?.id || null,
                reactions: []
            };

            // Replace temp message with real one from backend
            setMessages(prev =>
                prev.map(msg => msg.id === tempId ? finalMessage : msg)
            );

            // Update conversation's last_message so the sidebar shows the new message
            // Mark as locally modified to preserve this update
            locallyModifiedConvsRef.current.add(activeConversationId);
            setConversations(prevConvs => prevConvs.map(conv => {
                if (conv.id === activeConversationId) {
                    return {
                        ...conv,
                        last_message: {
                            id: finalMessage.id,
                            text: finalMessage.text || finalMessage.message || finalMessage.content || '',
                            sender_email: finalMessage.sender_email,
                            created_at: finalMessage.created_at
                        },
                        last_message_at: finalMessage.created_at
                    };
                }
                return conv;
            }));
        } catch (error) {
            console.error("Failed to send message:", error);
            // Revert optimistic update or show error
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === tempId ? { ...msg, status: 'error' } : msg
                )
            );
            // Restore state so user can try again
            setMessageInput(text);
            setAttachments(currentAttachments);
            setPendingPreviews(currentPreviews);
            if (currentReplyTo) setReplyingTo(currentReplyTo);
        } finally {
            setIsSending(false);
            // Ensure focus is kept on input
            setTimeout(() => {
                // When embedded, use preventScroll to avoid triggering parent page scroll
                inputRef.current?.focus({ preventScroll: isEmbedded });
            }, 10);
        }
    };


    const onEmojiClick = (emojiObject) => {
        setMessageInput(prev => prev + emojiObject.emoji);
        // When embedded, use preventScroll to avoid triggering parent page scroll
        inputRef.current?.focus({ preventScroll: isEmbedded });
    };

    const handleReaction = async (msgId, emoji) => {
        // Optimistic update
        setMessages(prev => prev.map(msg => {
            if (msg.id !== msgId) return msg;

            const reactions = msg.reactions || [];
            const existingReaction = reactions.find(r => r.emoji === emoji);

            let newReactions;
            if (existingReaction) {
                // Toggle off if user already reacted
                if (existingReaction.userReacted) {
                    newReactions = reactions.map(r =>
                        r.emoji === emoji
                            ? { ...r, count: r.count - 1, userReacted: false }
                            : r
                    ).filter(r => r.count > 0);
                } else {
                    // Add user's reaction
                    newReactions = reactions.map(r =>
                        r.emoji === emoji
                            ? { ...r, count: r.count + 1, userReacted: true }
                            : r
                    );
                }
            } else {
                // New reaction
                newReactions = [...reactions, { emoji, count: 1, userReacted: true }];
            }

            return { ...msg, reactions: newReactions };
        }));

        // Persist to backend
        try {
            await ChatService.toggleReaction(msgId, emoji);
        } catch (error) {
            console.error("Failed to persist reaction:", error);
            // Revert on error
            setMessages(prev => prev.map(msg => {
                if (msg.id !== msgId) return msg;
                // Revert the optimistic update
                return msg;
            }));
        }
    };

    // === NEW ROCKET.CHAT-INSPIRED MESSAGE ACTIONS ===

    // Copy message text to clipboard
    const handleCopyMessage = async (msg) => {
        const text = msg.text || msg.message || msg.content || '';
        try {
            await navigator.clipboard.writeText(text);
            // Could add a toast notification here
            setMessageActionsMenu(null);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Pin/Unpin message
    const handlePinMessage = async (msg) => {
        const isPinned = pinnedMessages.some(p => p.id === msg.id);

        if (isPinned) {
            setPinnedMessages(prev => prev.filter(p => p.id !== msg.id));
        } else {
            setPinnedMessages(prev => [...prev, msg]);
        }

        setMessageActionsMenu(null);

        // Persist to backend
        try {
            await ChatService.pinMessage(msg.id, !isPinned);
        } catch (error) {
            console.error("Failed to pin message:", error);
            // Revert on error
            if (isPinned) {
                setPinnedMessages(prev => [...prev, msg]);
            } else {
                setPinnedMessages(prev => prev.filter(p => p.id !== msg.id));
            }
        }
    };

    // Star/Unstar message
    const handleStarMessage = async (msg) => {
        const isStarred = starredMessages.some(s => s.id === msg.id);

        if (isStarred) {
            setStarredMessages(prev => prev.filter(s => s.id !== msg.id));
        } else {
            setStarredMessages(prev => [...prev, msg]);
        }

        setMessageActionsMenu(null);

        // Persist to backend
        try {
            await ChatService.starMessage(msg.id, !isStarred);
        } catch (error) {
            console.error("Failed to star message:", error);
            // Revert on error
            if (isStarred) {
                setStarredMessages(prev => [...prev, msg]);
            } else {
                setStarredMessages(prev => prev.filter(s => s.id !== msg.id));
            }
        }
    };

    // Start editing a message
    const handleEditMessage = (msg) => {
        setEditingMessage(msg);
        setEditText(msg.text || msg.message || msg.content || '');
        setMessageActionsMenu(null);
    };

    // Save edited message
    const handleSaveEdit = async () => {
        if (!editingMessage || !editText.trim()) return;

        const originalText = editingMessage.text || editingMessage.message || editingMessage.content;

        // Optimistic update
        setMessages(prev => prev.map(msg =>
            msg.id === editingMessage.id
                ? { ...msg, text: editText.trim(), edited: true, edited_at: new Date().toISOString() }
                : msg
        ));

        setEditingMessage(null);
        setEditText('');

        // Persist to backend
        try {
            await ChatService.editMessage(editingMessage.id, editText.trim());
        } catch (error) {
            console.error("Failed to edit message:", error);
            // Revert on error
            setMessages(prev => prev.map(msg =>
                msg.id === editingMessage.id
                    ? { ...msg, text: originalText, edited: editingMessage.edited }
                    : msg
            ));
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingMessage(null);
        setEditText('');
    };

    // Delete individual message
    const handleDeleteMessage = async () => {
        if (!messageToDelete) return;

        const msgId = messageToDelete.id;
        const msgToRestore = messageToDelete;

        // Track as deleted locally (prevents re-fetch from bringing it back)
        setDeletedMessageIds(prev => new Set([...prev, msgId]));
        deletedMessageIdsRef.current = new Set([...deletedMessageIdsRef.current, msgId]);
        // Also track globally to prevent server overwriting conversation last_message
        globalDeletedMessageIdsRef.current.add(msgId);

        // Mark conversation as locally modified to prevent server overwriting last_message
        if (activeConversationId) {
            locallyModifiedConvsRef.current.add(activeConversationId);
        }

        // Optimistic update - remove from messages
        setMessages(prev => {
            const remaining = prev.filter(msg => msg.id !== msgId);

            // Update conversation's last_message if we deleted the last message
            if (activeConversationId) {
                const newLastMessage = remaining.length > 0
                    ? remaining[remaining.length - 1]
                    : null;

                setConversations(convs => convs.map(conv => {
                    if (conv.id === activeConversationId) {
                        return {
                            ...conv,
                            last_message: newLastMessage ? {
                                id: newLastMessage.id,
                                text: newLastMessage.text || newLastMessage.message || newLastMessage.content || '',
                                sender_email: newLastMessage.sender_email,
                                created_at: newLastMessage.created_at
                            } : null,
                            last_message_at: newLastMessage?.created_at || conv.created_at
                        };
                    }
                    return conv;
                }));
            }

            return remaining;
        });
        setMessageToDelete(null);

        // Persist to backend
        try {
            await ChatService.deleteMessage(msgId);
        } catch (error) {
            console.error("Failed to delete message:", error);
            // Restore the message on error
            setDeletedMessageIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(msgId);
                return newSet;
            });
            deletedMessageIdsRef.current.delete(msgId);
            setMessages(prev => [...prev, msgToRestore].sort((a, b) =>
                new Date(a.created_at) - new Date(b.created_at)
            ));
            // Also restore the conversation's last_message
            setConversations(convs => convs.map(conv => {
                if (conv.id === activeConversationId) {
                    return {
                        ...conv,
                        last_message: {
                            id: msgToRestore.id,
                            text: msgToRestore.text || msgToRestore.message || msgToRestore.content || '',
                            sender_email: msgToRestore.sender_email,
                            created_at: msgToRestore.created_at
                        },
                        last_message_at: msgToRestore.created_at
                    };
                }
                return conv;
            }));
        }
    };

    // Reply to a message (quote)
    const handleReplyToMessage = (msg) => {
        setReplyingTo(msg);
        setMessageActionsMenu(null);
        // When embedded, use preventScroll to avoid triggering parent page scroll
        inputRef.current?.focus({ preventScroll: isEmbedded });
    };

    // Cancel reply
    const handleCancelReply = () => {
        setReplyingTo(null);
    };

    // Forward message to another conversation
    const handleForwardMessage = async (msg, targetConversationId) => {
        // Prevent double-forward
        if (forwardingMessageId === msg.id) return;

        setForwardingMessageId(msg.id);
        setMessageActionsMenu(null);

        try {
            await ChatService.forwardMessage(msg.id, targetConversationId);
            // Success - don't switch conversations, just close menu
        } catch (error) {
            console.error("Failed to forward message:", error);
        } finally {
            setForwardingMessageId(null);
        }
    };

    // Handle @mention detection in input
    const handleMentionDetection = (text, cursorPosition) => {
        // Find if we're typing a mention
        const textBeforeCursor = text.slice(0, cursorPosition);
        const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

        if (mentionMatch) {
            const searchTerm = mentionMatch[1].toLowerCase();
            const participants = activeConversation?.participants || [];
            const suggestions = participants.filter(p =>
                p.email !== userEmail && (
                    p.name?.toLowerCase().includes(searchTerm) ||
                    p.email?.toLowerCase().includes(searchTerm)
                )
            ).slice(0, 5);

            setMentionSuggestions(suggestions);
            setShowMentionPopup(suggestions.length > 0);
            setMentionCursorPos(cursorPosition);
        } else {
            setShowMentionPopup(false);
            setMentionSuggestions([]);
        }
    };

    // Insert mention into input
    const insertMention = (member) => {
        const textBeforeCursor = messageInput.slice(0, mentionCursorPos);
        const textAfterCursor = messageInput.slice(mentionCursorPos);
        const mentionStart = textBeforeCursor.lastIndexOf('@');

        const newText = textBeforeCursor.slice(0, mentionStart) +
            `@${member.name?.split(' ')[0] || member.email.split('@')[0]} ` +
            textAfterCursor;

        setMessageInput(newText);
        setShowMentionPopup(false);
        setMentionSuggestions([]);
        // When embedded, use preventScroll to avoid triggering parent page scroll
        inputRef.current?.focus({ preventScroll: isEmbedded });
    };

    // Apply formatting to selected text
    const applyFormatting = (format) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selectedText = messageInput.slice(start, end);

        let formattedText = '';
        let cursorOffset = 0;

        switch (format) {
            case 'bold':
                formattedText = `**${selectedText || 'text'}**`;
                cursorOffset = selectedText ? 0 : -2;
                break;
            case 'italic':
                formattedText = `*${selectedText || 'text'}*`;
                cursorOffset = selectedText ? 0 : -1;
                break;
            case 'code':
                formattedText = `\`${selectedText || 'code'}\``;
                cursorOffset = selectedText ? 0 : -1;
                break;
            case 'codeblock':
                formattedText = `\`\`\`\n${selectedText || 'code'}\n\`\`\``;
                cursorOffset = selectedText ? 0 : -4;
                break;
            default:
                return;
        }

        const newText = messageInput.slice(0, start) + formattedText + messageInput.slice(end);
        setMessageInput(newText);

        // Set cursor position
        setTimeout(() => {
            const newPos = start + formattedText.length + cursorOffset;
            input.setSelectionRange(newPos, newPos);
            // When embedded, skip focus to prevent scroll issues
            if (!isEmbedded) {
                input.focus();
            }
        }, 0);
    };

    // Filter messages by search term
    const filteredMessages = useMemo(() => {
        if (!messageSearchTerm.trim()) return messages;

        const search = messageSearchTerm.toLowerCase();
        return messages.filter(msg => {
            const text = msg.text || msg.message || msg.content || '';
            return text.toLowerCase().includes(search);
        });
    }, [messages, messageSearchTerm]);

    // Group messages into threads - replies appear directly under their parent
    // Supports nested replies (reply to a reply goes under the same root thread)
    const threadedMessages = useMemo(() => {
        const messageMap = new Map();
        const replyIds = new Set();
        const processedIds = new Set(); // Track what we've already added

        // Build a map of all messages by ID
        filteredMessages.forEach(msg => {
            messageMap.set(msg.id, msg);
        });

        // Find the root message of any reply chain
        const findThreadRoot = (msgId, visited = new Set()) => {
            if (visited.has(msgId)) return msgId; // Prevent infinite loops
            visited.add(msgId);

            const msg = messageMap.get(msgId);
            if (!msg) return msgId;

            const parentId = msg.reply_to_id || msg.replyToId || msg.reply_to?.id;
            if (!parentId || !messageMap.has(parentId)) {
                return msgId; // This is a root message or parent not found
            }
            return findThreadRoot(parentId, visited);
        };

        // Group all replies by their thread root
        const repliesByRoot = new Map();

        filteredMessages.forEach(msg => {
            const replyToId = msg.reply_to_id || msg.replyToId || msg.reply_to?.id;
            if (replyToId) {
                replyIds.add(msg.id);
                const rootId = findThreadRoot(replyToId);

                // Only add if root is different from this message (prevent self-reference)
                if (rootId !== msg.id) {
                    if (!repliesByRoot.has(rootId)) {
                        repliesByRoot.set(rootId, []);
                    }
                    repliesByRoot.get(rootId).push(msg);
                }
            }
        });

        // Sort replies by time within each thread
        repliesByRoot.forEach(replies => {
            replies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        });

        // Build the final threaded list
        const result = [];
        filteredMessages.forEach(msg => {
            // Skip if this message is a reply (will be inserted after its root)
            if (replyIds.has(msg.id)) return;
            // Skip if already processed
            if (processedIds.has(msg.id)) return;

            // Add root message
            result.push(msg);
            processedIds.add(msg.id);

            // Add all replies in this thread directly after the root
            const replies = repliesByRoot.get(msg.id) || [];
            replies.forEach(reply => {
                if (!processedIds.has(reply.id)) {
                    result.push({ ...reply, _isReply: true });
                    processedIds.add(reply.id);
                }
            });
        });

        return result;
    }, [filteredMessages]);

    // Calculate reply counts for each root message (Slack-style thread indicator)
    const replyCountByRoot = useMemo(() => {
        const counts = new Map();
        filteredMessages.forEach(msg => {
            const replyToId = msg.reply_to_id || msg.replyToId || msg.reply_to?.id;
            if (replyToId) {
                // Find root of this reply chain
                let rootId = replyToId;
                let parentMsg = filteredMessages.find(m => m.id === replyToId);
                while (parentMsg) {
                    const parentReplyTo = parentMsg.reply_to_id || parentMsg.replyToId || parentMsg.reply_to?.id;
                    if (!parentReplyTo) break;
                    rootId = parentReplyTo;
                    parentMsg = filteredMessages.find(m => m.id === parentReplyTo);
                }
                counts.set(rootId, (counts.get(rootId) || 0) + 1);
            }
        });
        return counts;
    }, [filteredMessages]);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (messageActionsMenu && !e.target.closest('.message-actions-menu')) {
                setMessageActionsMenu(null);
            }
            if (showQuickReactions && !e.target.closest('.quick-reactions-bar')) {
                setShowQuickReactions(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [messageActionsMenu, showQuickReactions]);

    const activeConversation = useMemo(
        () => conversations.find(c => c.id === activeConversationId),
        [conversations, activeConversationId]
    );

    // Check if conversation is a group chat (more than 2 participants)
    const isGroupChat = activeConversation?.participants?.length > 2;

    // Memoize filtered conversations to avoid re-filtering on every render
    const filteredConversations = useMemo(() => {
        if (!searchTerm.trim()) return conversations;

        const search = searchTerm.toLowerCase();

        return conversations.filter(conv => {
            // Search in group name
            if (conv.name && conv.name.toLowerCase().includes(search)) return true;

            // Search in participant names
            const participantMatch = conv.participants.some(p =>
                (p.name && p.name.toLowerCase().includes(search)) ||
                (p.email && p.email.toLowerCase().includes(search))
            );
            if (participantMatch) return true;

            // Search in last message
            if (conv.last_message?.text && conv.last_message.text.toLowerCase().includes(search)) return true;

            return false;
        });
    }, [conversations, searchTerm]);

    // Check if message should show sender info (Slack-style grouping)
    const shouldShowSenderInfo = (msg, idx, messages) => {
        if (idx === 0) return true;
        const prevMsg = messages[idx - 1];
        if (prevMsg.sender_email !== msg.sender_email) return true;

        // Show sender info if messages are >5 minutes apart
        const timeDiff = new Date(msg.created_at) - new Date(prevMsg.created_at);
        return timeDiff > 300000; // 5 minutes
    };

    // Check if we should show date separator
    const shouldShowDateSeparator = (msg, idx, messages) => {
        if (idx === 0) return true;
        const prevMsg = messages[idx - 1];
        const currentDate = new Date(msg.created_at).toDateString();
        const prevDate = new Date(prevMsg.created_at).toDateString();
        return currentDate !== prevDate;
    };

    // Format date separator
    const formatDateSeparator = (timestamp) => {
        const msgDate = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (msgDate.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (msgDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return msgDate.toLocaleDateString([], {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: msgDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-1 min-h-0 h-full dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
                <div className="w-80 border-r dark:border-slate-800 border-slate-200 dark:bg-slate-900/30 bg-slate-50 flex flex-col animate-pulse">
                    <div className="p-4 border-b dark:border-slate-800 border-slate-200 space-y-3">
                        <div className="h-6 dark:bg-slate-800 bg-slate-200 rounded w-32"></div>
                        <div className="h-10 dark:bg-slate-800 bg-slate-200 rounded-lg"></div>
                    </div>
                    <div className="flex-1 p-3 space-y-2">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-3 p-3">
                                <div className="h-10 w-10 dark:bg-slate-800 bg-slate-200 rounded-full"></div>
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 dark:bg-slate-800 bg-slate-200 rounded w-2/3"></div>
                                    <div className="h-3 dark:bg-slate-800 bg-slate-200 rounded w-1/2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex-1 flex flex-col dark:bg-slate-950/30 bg-white">
                    <div className="h-16 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-white"></div>
                    <div className="flex-1 p-6 space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-2">
                                <div className="h-8 w-8 dark:bg-slate-800 bg-slate-200 rounded-lg"></div>
                                <div className="space-y-1 flex-1">
                                    <div className="h-3 dark:bg-slate-800 bg-slate-200 rounded w-24"></div>
                                    <div className="h-10 dark:bg-slate-800 bg-slate-200 rounded-lg w-2/3"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-1 min-h-0 h-full dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden shadow-2xl relative">
            {/* Error Toast */}
            {loadError && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 max-w-md animate-in slide-in-from-top-4 fade-in duration-300">
                    <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 backdrop-blur-xl shadow-xl">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center mt-0.5">
                                <X size={12} className="text-red-400" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-red-400 mb-1">Team Chat Error</p>
                                <p className="text-xs text-red-300/90">{loadError}</p>
                                {loadError.includes("Session expired") ? (
                                    <p className="text-xs text-red-400/60 mt-2">Sign out from the menu and sign back in</p>
                                ) : (
                                    <p className="text-xs text-red-400/60 mt-2">Press F12 and check Console tab for details</p>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setLoadError(null);
                                    chatAuthErrorOccurred = false; // Reset auth error flag
                                    window.location.reload();
                                }}
                                className="flex-shrink-0 px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded transition"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Chat Modal */}
            {isNewChatOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xl font-semibold dark:text-white text-slate-900">New Message</h3>
                                {selectedMembers.length > 0 && (
                                    <p className="text-sm text-sky-500 mt-1">
                                        {selectedMembers.length} selected {selectedMembers.length > 1 && '(Group Chat)'}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setIsNewChatOpen(false);
                                    setSelectedMembers([]);
                                    setGroupChatName("");
                                }}
                                className="p-2 dark:hover:bg-slate-800 hover:bg-slate-100 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 transition"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Group Name/Topic Input (shown for all conversations) */}
                        {selectedMembers.length > 0 && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                    Group Name/Topic (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={groupChatName}
                                    onChange={(e) => setGroupChatName(e.target.value)}
                                    placeholder={selectedMembers.length > 1
                                        ? "e.g., Project Team, Marketing Discussion"
                                        : "e.g., Loan Review, Client Discussion"}
                                    className="w-full dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-700/50 border-slate-200 rounded-lg px-4 py-2.5 text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20 transition"
                                    maxLength={50}
                                />
                                <p className="text-xs text-slate-500 mt-1.5">
                                    {selectedMembers.length > 1
                                        ? "Give your conversation a memorable name"
                                        : "Note: Group names only work with 2+ people. This field is for your reference."}
                                </p>
                            </div>
                        )}

                        {isLoadingTeam ? (
                            <div className="text-center py-12 text-slate-400">
                                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-sky-500 border-r-transparent"></div>
                                <p className="mt-3">Loading team members...</p>
                            </div>
                        ) : teamMembers.length === 0 ? (
                            <div className="text-center py-12">
                                <Users size={48} className="mx-auto text-slate-600 mb-3" />
                                <p className="text-slate-400 mb-2">No team members found</p>
                                <p className="text-sm text-slate-500">Invite members in the Teams section first</p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2 max-h-96 overflow-y-auto mb-4 pr-1">
                                    {teamMembers.map(member => {
                                        const isSelected = selectedMembers.some(m => m.email === member.email);
                                        return (
                                            <button
                                                key={member.email}
                                                onClick={() => toggleMemberSelection(member)}
                                                className={`w-full p-3 rounded-xl text-left transition-all flex items-center gap-3 ${isSelected
                                                    ? 'bg-sky-600 text-white shadow-lg scale-[0.98]'
                                                    : 'dark:bg-slate-800/40 bg-slate-100 dark:text-slate-200 text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800 hover:scale-[0.99]'
                                                    }`}
                                            >
                                                <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-sm font-semibold ${isSelected ? 'bg-white/20' : 'dark:bg-slate-700 bg-slate-200 dark:text-slate-200 text-slate-600'
                                                    }`}>
                                                    {member.name?.[0] || member.email[0].toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">{member.name || member.email}</div>
                                                    <div className="text-xs opacity-75 truncate">{member.email}</div>
                                                </div>
                                                {isSelected && (
                                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-sky-600 flex items-center justify-center font-bold">
                                                        ✓
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            setIsNewChatOpen(false);
                                            setSelectedMembers([]);
                                            setGroupChatName("");
                                        }}
                                        className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-xl transition font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleStartChat}
                                        disabled={selectedMembers.length === 0}
                                        className="flex-1 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-sky-900/50"
                                    >
                                        Start Chat
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Message Confirmation Modal */}
            {messageToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                                <Trash2 size={24} className="text-red-500" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">Delete Message</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-600">
                                    Are you sure you want to delete this message?
                                </p>
                            </div>
                        </div>

                        <div className="dark:bg-slate-800/50 bg-slate-100 rounded-lg p-3 mb-4">
                            <p className="text-sm dark:text-slate-300 text-slate-700 line-clamp-3">
                                {messageToDelete.text || messageToDelete.message || messageToDelete.content}
                            </p>
                        </div>

                        <div className="dark:bg-red-500/10 bg-red-50 border dark:border-red-500/20 border-red-200 rounded-lg p-3 mb-6">
                            <p className="text-xs dark:text-red-400 text-red-600">
                                This action cannot be undone.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setMessageToDelete(null)}
                                className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-xl transition font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteMessage}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl transition font-medium shadow-lg shadow-red-900/30 flex items-center justify-center gap-2"
                            >
                                <Trash2 size={16} />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Conversation Confirmation Modal */}
            {conversationToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                                <AlertTriangle size={24} className="text-red-500" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">Delete Conversation</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-600">
                                    Are you sure you want to delete this conversation with{' '}
                                    <span className="font-medium dark:text-slate-200 text-slate-800">
                                        {getConversationDisplayName(conversationToDelete, userEmail)}
                                    </span>
                                    ?
                                </p>
                            </div>
                        </div>

                        <div className="dark:bg-red-500/10 bg-red-50 border dark:border-red-500/20 border-red-200 rounded-lg p-3 mb-6">
                            <p className="text-xs dark:text-red-400 text-red-600">
                                This action cannot be undone. All messages will be permanently deleted for all participants.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setConversationToDelete(null)}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-xl transition font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConversation}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-900/30 flex items-center justify-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Member to Group Modal */}
            {showAddMemberModal && activeConversation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-xl font-semibold dark:text-white text-slate-900">Add Member</h3>
                                <p className="text-sm text-slate-500 mt-1">
                                    Add a team member to "{getConversationDisplayName(activeConversation, userEmail)}"
                                </p>
                            </div>
                            <button
                                onClick={() => setShowAddMemberModal(false)}
                                disabled={isAddingMember}
                                className="p-2 dark:hover:bg-slate-800 hover:bg-slate-100 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 transition disabled:opacity-50"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {isLoadingTeam ? (
                            <div className="text-center py-12 text-slate-400">
                                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-sky-500 border-r-transparent"></div>
                                <p className="mt-3">Loading team members...</p>
                            </div>
                        ) : (() => {
                            // Filter out members already in the conversation
                            const currentParticipantEmails = activeConversation.participants.map(p => p.email);
                            const availableMembers = teamMembers.filter(m => !currentParticipantEmails.includes(m.email));

                            if (availableMembers.length === 0) {
                                return (
                                    <div className="text-center py-12">
                                        <Users size={48} className="mx-auto text-slate-600 mb-3" />
                                        <p className="text-slate-400 mb-2">No available members to add</p>
                                        <p className="text-sm text-slate-500">All team members are already in this conversation</p>
                                    </div>
                                );
                            }

                            return (
                                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                                    {availableMembers.map(member => (
                                        <button
                                            key={member.email}
                                            onClick={() => handleAddMemberToGroup(member.email)}
                                            disabled={isAddingMember}
                                            className="w-full p-3 rounded-xl text-left transition-all flex items-center gap-3 dark:bg-slate-800/40 bg-slate-100 dark:text-slate-200 text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800 hover:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="h-10 w-10 rounded-xl flex items-center justify-center text-sm font-semibold dark:bg-slate-700 bg-slate-200 dark:text-slate-200 text-slate-600">
                                                {member.name?.[0] || member.email[0].toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium truncate">{member.name || member.email}</div>
                                                <div className="text-xs opacity-75 truncate">{member.email}</div>
                                            </div>
                                            {isAddingMember ? (
                                                <Loader2 size={18} className="text-sky-500 animate-spin" />
                                            ) : (
                                                <UserPlus size={18} className="text-sky-500" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            );
                        })()}

                        <div className="mt-4">
                            <button
                                onClick={() => setShowAddMemberModal(false)}
                                disabled={isAddingMember}
                                className="w-full px-4 py-2.5 dark:bg-slate-800 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-xl transition font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove/Leave Member Confirmation Modal */}
            {memberToRemove && activeConversation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-start gap-4 mb-4">
                            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${memberToRemove.email === userEmail ? 'bg-orange-500/10' : 'bg-red-500/10'}`}>
                                {memberToRemove.email === userEmail ? (
                                    <LogOut size={24} className="text-orange-500" />
                                ) : (
                                    <UserMinus size={24} className="text-red-500" />
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">
                                    {memberToRemove.email === userEmail ? 'Leave Group' : 'Remove Member'}
                                </h3>
                                <p className="text-sm dark:text-slate-400 text-slate-600">
                                    {memberToRemove.email === userEmail ? (
                                        <>Are you sure you want to leave <span className="font-medium dark:text-slate-200 text-slate-800">"{getConversationDisplayName(activeConversation, userEmail)}"</span>?</>
                                    ) : (
                                        <>Remove <span className="font-medium dark:text-slate-200 text-slate-800">{memberToRemove.name || memberToRemove.email}</span> from the group?</>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="dark:bg-slate-800/50 bg-slate-100 rounded-lg p-3 mb-4 flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold bg-gradient-to-br from-slate-600 to-slate-700 text-white">
                                {memberToRemove.name?.[0]?.toUpperCase() || memberToRemove.email?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                                <p className="text-sm font-medium dark:text-slate-200 text-slate-800">
                                    {memberToRemove.name || memberToRemove.email?.split('@')[0]}
                                </p>
                                <p className="text-xs text-slate-500">{memberToRemove.email}</p>
                            </div>
                        </div>

                        <div className={`${memberToRemove.email === userEmail ? 'dark:bg-orange-500/10 bg-orange-50 border dark:border-orange-500/20 border-orange-200' : 'dark:bg-red-500/10 bg-red-50 border dark:border-red-500/20 border-red-200'} rounded-lg p-3 mb-6`}>
                            <p className={`text-xs ${memberToRemove.email === userEmail ? 'dark:text-orange-400 text-orange-600' : 'dark:text-red-400 text-red-600'}`}>
                                {memberToRemove.email === userEmail
                                    ? "You will no longer be able to see messages in this conversation."
                                    : "This member will no longer be able to see messages in this conversation."
                                }
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setMemberToRemove(null)}
                                disabled={isRemovingMember}
                                className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-xl transition font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleRemoveMemberFromGroup(memberToRemove.email)}
                                disabled={isRemovingMember}
                                className={`flex-1 px-4 py-2.5 text-white rounded-xl transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2 ${
                                    memberToRemove.email === userEmail
                                        ? 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/30'
                                        : 'bg-red-600 hover:bg-red-500 shadow-red-900/30'
                                }`}
                            >
                                {isRemovingMember ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {memberToRemove.email === userEmail ? 'Leaving...' : 'Removing...'}
                                    </>
                                ) : (
                                    <>
                                        {memberToRemove.email === userEmail ? <LogOut size={16} /> : <UserMinus size={16} />}
                                        {memberToRemove.email === userEmail ? 'Leave Group' : 'Remove'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar - Conversations */}
            <div className="w-80 border-r dark:border-slate-800 border-slate-200 dark:bg-slate-900/30 bg-slate-50 flex flex-col min-h-0">
                <div className="p-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-slate-100/50">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-bold dark:text-white text-slate-900">Messages</h2>
                        <div className="flex items-center gap-1">
                            {/* Notification Bell */}
                            <button
                                onClick={handleNotificationToggle}
                                className={`p-2 rounded-lg transition-all hover:scale-105 ${notificationPermission === 'granted'
                                        ? 'text-sky-500 hover:text-sky-400 hover:bg-sky-500/10'
                                        : notificationPermission === 'denied'
                                            ? 'text-slate-600 hover:text-red-500 hover:bg-red-500/10'
                                            : 'text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800'
                                    }`}
                                title={
                                    notificationPermission === 'granted'
                                        ? 'Notifications enabled'
                                        : notificationPermission === 'denied'
                                            ? 'Notifications blocked - click to learn more'
                                            : 'Enable notifications'
                                }
                            >
                                {notificationPermission === 'granted' ? (
                                    <Bell size={18} className="fill-current" />
                                ) : (
                                    <BellOff size={18} />
                                )}
                            </button>

                            {/* New Chat Button */}
                            <button
                                onClick={() => isFreeUser && onUpgradePlan ? onUpgradePlan() : setIsNewChatOpen(true)}
                                className={`p-2 rounded-lg transition-all hover:scale-105 ${isFreeUser ? 'text-slate-600 hover:text-amber-600 hover:bg-amber-100 dark:hover:text-amber-400 dark:hover:bg-amber-500/10' : 'dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800'}`}
                                title={isFreeUser ? "Upgrade to create new chats" : "New message"}
                            >
                                <Plus size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 h-4 w-4 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full dark:bg-slate-950/50 bg-white border dark:border-slate-700/50 border-slate-200 rounded-lg py-2 pl-9 pr-4 text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-500 dark:placeholder:text-slate-600 focus:outline-none focus:border-sky-500/50 focus:bg-white dark:focus:bg-slate-950 transition"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition"
                                title="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {conversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-6 animate-in fade-in duration-300">
                            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-sky-500/10 to-blue-600/10 flex items-center justify-center mb-4">
                                <MessageSquare size={40} className="text-sky-500/60" />
                            </div>
                            <h3 className="text-base font-semibold dark:text-white text-slate-900 mb-1">No conversations yet</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-xs">
                                Start chatting with your team members to collaborate in real-time
                            </p>
                            <button
                                onClick={() => setIsNewChatOpen(true)}
                                className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-sky-900/30"
                            >
                                Start your first chat
                            </button>
                        </div>
                    ) : filteredConversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-6 animate-in fade-in duration-300">
                            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-slate-500/10 to-slate-600/10 flex items-center justify-center mb-4">
                                <Search size={40} className="text-slate-500/60" />
                            </div>
                            <h3 className="text-base font-semibold dark:text-white text-slate-900 mb-1">No conversations found</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                Try a different search term
                            </p>
                            <button
                                onClick={() => setSearchTerm("")}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium transition-all hover:scale-105"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        filteredConversations.map(conv => (
                            <div
                                key={conv.id}
                                className={`group relative w-full p-3 flex items-start gap-3 transition-all duration-200 border-l-2 cursor-pointer ${activeConversationId === conv.id
                                    ? 'dark:bg-slate-800/80 bg-sky-50 border-l-sky-500'
                                    : conv.unread_count > 0
                                        ? 'bg-sky-500/10 border-l-sky-400 hover:bg-sky-500/20'
                                        : 'border-l-transparent hover:bg-slate-100 dark:hover:bg-slate-800/40 hover:border-l-slate-300 dark:hover:border-l-slate-700'
                                    }`}
                                onClick={() => handleConversationSelect(conv)}
                            >
                                {/* Delete button - shows on hover */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setConversationToDelete(conv);
                                    }}
                                    className="absolute right-2 top-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 dark:bg-slate-800/90 bg-white/90 dark:hover:bg-red-500/20 hover:bg-red-50 dark:text-slate-400 text-slate-500 dark:hover:text-red-400 hover:text-red-500 shadow-sm z-10"
                                    title="Delete conversation"
                                >
                                    <Trash2 size={14} />
                                </button>
                                <div className="relative">
                                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${activeConversationId === conv.id ? 'bg-sky-600/20 text-sky-600 dark:text-sky-400' : 'dark:bg-slate-800 bg-slate-200 dark:text-slate-400 text-slate-500'
                                        }`}>
                                        {(() => {
                                            const otherParticipant = conv.participants.find(p => p.email !== userEmail);
                                            const avatarUrl = conv.type === 'team' ? null : (otherParticipant?.avatar_url || otherParticipant?.avatarUrl);

                                            if (avatarUrl) {
                                                return <img src={avatarUrl} alt="" className="h-full w-full object-cover" />;
                                            }

                                            return conv.type === 'team' ? <Users size={18} /> : <User size={18} />;
                                        })()}
                                    </div>
                                    {conv.unread_count > 0 && (
                                        <div className="absolute -top-1 -right-1 bg-sky-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center border-2 dark:border-slate-900 border-white">
                                            {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                        </div>
                                    )}
                                    {/* Online status indicator (Slack-style green dot) */}
                                    {conv.type !== 'team' && !conv.unread_count && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 dark:border-slate-900 border-white" title="Online" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <h3 className={`text-sm dark:text-white text-slate-900 truncate ${conv.unread_count > 0 ? 'font-bold' : 'font-semibold'}`}>
                                            {getConversationDisplayName(conv, userEmail)}
                                        </h3>
                                        <span className={`text-xs flex-shrink-0 group-hover:opacity-0 transition-opacity ${conv.unread_count > 0 ? 'text-sky-500 dark:text-sky-400 font-medium' : 'text-slate-500'}`}>
                                            {conv.last_message?.created_at && getRelativeTime(conv.last_message.created_at)}
                                        </span>
                                    </div>
                                    <p className={`text-xs truncate ${conv.unread_count > 0 ? 'dark:text-slate-300 text-slate-700 font-medium' : 'dark:text-slate-400 text-slate-500'}`}>
                                        {conv.last_message?.sender_email === userEmail ? 'You: ' : (
                                            conv.last_message?.sender_email && conv.participants.length > 2 ? (
                                                // Show sender name in group chats
                                                `${conv.participants.find(p => p.email === conv.last_message.sender_email)?.name?.split(' ')[0] || conv.last_message.sender_email.split('@')[0]}: `
                                            ) : ''
                                        )}
                                        {getMessagePreviewText(conv.last_message)}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col bg-gradient-to-br dark:from-slate-950/40 dark:to-slate-900/40 from-slate-50 to-white min-h-0">
                {activeConversation ? (
                    <>
                        {/* Chat Header */}
                        <div className="h-16 px-6 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between dark:bg-slate-900/60 bg-white/60 backdrop-blur-sm">
                            <button
                                onClick={() => setShowMembersPanel(!showMembersPanel)}
                                className="flex items-center gap-3 hover:bg-slate-800/30 dark:hover:bg-slate-800/50 rounded-lg px-2 py-1.5 -ml-2 transition-colors group/header"
                            >
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-sky-500/20 to-blue-600/20 flex items-center justify-center">
                                    <Users size={20} className="text-sky-500" />
                                </div>
                                <div className="text-left">
                                    <h3 className="text-sm font-bold dark:text-white text-slate-900">
                                        {getConversationDisplayName(activeConversation, userEmail)}
                                    </h3>
                                    <p className="text-xs text-slate-400 group-hover/header:text-sky-400 transition-colors">
                                        {activeConversation.participants.length} {activeConversation.participants.length === 1 ? 'member' : 'members'}
                                        {pinnedMessages.length > 0 && ` • ${pinnedMessages.length} pinned`}
                                        <span className="ml-1 opacity-0 group-hover/header:opacity-100 transition-opacity">• Click to view</span>
                                    </p>
                                </div>
                            </button>

                            {/* Header Actions */}
                            <div className="flex items-center gap-2">
                                {/* Message Search Toggle */}
                                <button
                                    onClick={() => setShowMessageSearch(!showMessageSearch)}
                                    className={`p-2 rounded-lg transition-all ${showMessageSearch
                                        ? 'bg-sky-500/20 text-sky-500'
                                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                                    }`}
                                    title="Search messages"
                                >
                                    <Search size={18} />
                                </button>

                                {/* Pinned Messages Toggle */}
                                {pinnedMessages.length > 0 && (
                                    <button
                                        onClick={() => setShowPinnedPanel(!showPinnedPanel)}
                                        className={`p-2 rounded-lg transition-all flex items-center gap-1 ${showPinnedPanel
                                            ? 'bg-amber-500/20 text-amber-500'
                                            : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800/50'
                                        }`}
                                        title="Pinned messages"
                                    >
                                        <Pin size={18} />
                                        <span className="text-xs font-medium">{pinnedMessages.length}</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Message Search Bar */}
                        {showMessageSearch && (
                            <div className="px-4 py-2 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/40 bg-slate-50">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 h-4 w-4" />
                                    <input
                                        type="text"
                                        value={messageSearchTerm}
                                        onChange={(e) => setMessageSearchTerm(e.target.value)}
                                        placeholder="Search in conversation..."
                                        className="w-full dark:bg-slate-950/50 bg-white border dark:border-slate-700/50 border-slate-200 rounded-lg py-2 pl-9 pr-4 text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50"
                                        autoFocus
                                    />
                                    {messageSearchTerm && (
                                        <button
                                            onClick={() => setMessageSearchTerm('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                {messageSearchTerm && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} found
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Pinned Messages Panel */}
                        {showPinnedPanel && pinnedMessages.length > 0 && (
                            <div className="px-4 py-3 border-b dark:border-slate-800 border-slate-200 dark:bg-amber-500/5 bg-amber-50 max-h-48 overflow-y-auto">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                        <Pin size={12} /> Pinned Messages
                                    </h4>
                                    <button
                                        onClick={() => setShowPinnedPanel(false)}
                                        className="text-slate-500 hover:text-slate-300"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {pinnedMessages.map(msg => (
                                        <div
                                            key={msg.id}
                                            className="dark:bg-slate-800/50 bg-white p-2 rounded-lg border dark:border-slate-700/50 border-slate-200 cursor-pointer hover:border-amber-500/50 transition"
                                            onClick={() => {
                                                // Scroll to message - skip when embedded to prevent parent scroll
                                                if (isEmbedded) return;
                                                const el = document.getElementById(`msg-${msg.id}`);
                                                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }}
                                        >
                                            <p className="text-xs text-slate-500 mb-1">
                                                {msg.sender_name || msg.sender_email?.split('@')[0]}
                                            </p>
                                            <p className="text-sm dark:text-slate-200 text-slate-700 line-clamp-2">
                                                {msg.text || msg.message || msg.content}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Members Panel */}
                        {showMembersPanel && (
                            <div className="px-4 py-3 border-b dark:border-slate-800 border-slate-200 dark:bg-sky-500/5 bg-sky-50 max-h-80 overflow-y-auto animate-in slide-in-from-top-2 duration-200">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-xs font-semibold text-sky-600 dark:text-sky-400 flex items-center gap-1.5">
                                        <Users size={12} />
                                        Chat Members ({activeConversation.participants.length})
                                    </h4>
                                    <div className="flex items-center gap-1">
                                        {/* Add Member Button - only for group chats */}
                                        {isGroupChat && (
                                            <button
                                                onClick={() => {
                                                    loadTeamMembers();
                                                    setShowAddMemberModal(true);
                                                }}
                                                className="p-1.5 rounded-lg text-sky-500 hover:text-sky-400 hover:bg-sky-500/10 transition-all flex items-center gap-1"
                                                title="Add member to group"
                                            >
                                                <UserPlus size={14} />
                                                <span className="text-xs font-medium">Add</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowMembersPanel(false)}
                                            className="text-slate-500 hover:text-slate-300 p-1 rounded hover:bg-slate-700/30 transition-colors"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    {activeConversation.participants.map((participant, idx) => {
                                        const isCurrentUser = participant.email === userEmail;
                                        const isBeingRemoved = isRemovingMember === participant.email;
                                        return (
                                            <div
                                                key={participant.email || idx}
                                                className={`group/member flex items-center gap-3 p-2.5 rounded-lg transition-all ${
                                                    isCurrentUser
                                                        ? 'dark:bg-sky-500/10 bg-sky-100 border dark:border-sky-500/20 border-sky-200'
                                                        : 'dark:bg-slate-800/50 bg-white border dark:border-slate-700/50 border-slate-200 hover:border-sky-500/40 hover:shadow-sm'
                                                } ${isBeingRemoved ? 'opacity-50' : ''}`}
                                            >
                                                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                                                    isCurrentUser
                                                        ? 'bg-gradient-to-br from-sky-500 to-blue-600 text-white'
                                                        : 'bg-gradient-to-br from-slate-600 to-slate-700 dark:from-slate-500 dark:to-slate-600 text-white'
                                                }`}>
                                                    {participant.name?.[0]?.toUpperCase() || participant.email?.[0]?.toUpperCase() || '?'}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium dark:text-white text-slate-900 truncate flex items-center gap-2">
                                                        {participant.name || participant.email?.split('@')[0] || 'Unknown'}
                                                        {isCurrentUser && (
                                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-500 dark:text-sky-400">
                                                                You
                                                            </span>
                                                        )}
                                                        {participant.role && (
                                                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full dark:bg-slate-700 bg-slate-200 dark:text-slate-300 text-slate-600">
                                                                {participant.role}
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-slate-500 truncate">
                                                        {participant.email || 'No email'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {/* Copy email button - show on hover for non-current users */}
                                                    {!isCurrentUser && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (participant.email) {
                                                                    navigator.clipboard.writeText(participant.email);
                                                                }
                                                            }}
                                                            className="opacity-0 group-hover/member:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 transition-all"
                                                            title="Copy email"
                                                        >
                                                            <Copy size={14} />
                                                        </button>
                                                    )}
                                                    {/* Remove member button - only for group chats */}
                                                    {isGroupChat && !isCurrentUser && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMemberToRemove(participant);
                                                            }}
                                                            disabled={isBeingRemoved}
                                                            className="opacity-0 group-hover/member:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50"
                                                            title="Remove from group"
                                                        >
                                                            {isBeingRemoved ? <Loader2 size={14} className="animate-spin" /> : <UserMinus size={14} />}
                                                        </button>
                                                    )}
                                                    {/* Leave group button - for current user in group chats */}
                                                    {isGroupChat && isCurrentUser && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMemberToRemove(participant);
                                                            }}
                                                            disabled={isBeingRemoved}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 transition-all disabled:opacity-50"
                                                            title="Leave group"
                                                        >
                                                            {isBeingRemoved ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Messages Area */}
                        <div
                            ref={messagesContainerRef}
                            className="flex-1 overflow-y-auto p-4 space-y-1 relative"
                            style={{ scrollBehavior: isEmbedded ? 'auto' : 'smooth' }}
                            onScroll={handleScroll}
                        >
                            {isLoadingMessages ? (
                                <div className="flex flex-col items-center justify-center h-full">
                                    <Loader2 size={32} className="text-sky-500 animate-spin mb-3" />
                                    <p className="text-sm text-slate-400">Loading messages...</p>
                                </div>
                            ) : threadedMessages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center animate-in fade-in duration-500">
                                    <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-sky-500/10 to-blue-600/10 flex items-center justify-center mb-4 animate-in zoom-in duration-300">
                                        <MessageSquare size={48} className="text-sky-500/40" />
                                    </div>
                                    <h3 className="dark:text-white text-slate-900 font-semibold text-lg mb-2">Start the conversation</h3>
                                    <p className="text-sm dark:text-slate-400 text-slate-500 max-w-sm mb-6">
                                        Send your first message to <span className="font-medium dark:text-slate-300 text-slate-700">{getConversationDisplayName(activeConversation, userEmail)}</span>
                                    </p>
                                    <div className="flex flex-wrap gap-2 justify-center max-w-md">
                                        {['👋 Hi there!', '💼 Let\'s discuss this', '📄 About the documents'].map((suggestion, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setMessageInput(suggestion)}
                                                className="px-3 py-1.5 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full transition-all hover:scale-105"
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Load Older Messages Button */}
                                    {hasMoreMessages && (
                                        <div className="flex justify-center py-3">
                                            <button
                                                onClick={loadOlderMessages}
                                                disabled={isLoadingOlderMessages}
                                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors disabled:opacity-50"
                                            >
                                                {isLoadingOlderMessages ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Loading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronUp size={16} />
                                                        Load older messages
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                    {threadedMessages.map((msg, idx) => {
                                    const isMe = msg.sender_email === userEmail;
                                    const isReply = msg._isReply;
                                    // For replies, always show sender since they're in a thread
                                    const showSender = isReply ? true : shouldShowSenderInfo(msg, idx, threadedMessages);
                                    // Don't show date separator for replies
                                    const showDate = isReply ? false : shouldShowDateSeparator(msg, idx, threadedMessages);
                                    const isPinned = pinnedMessages.some(p => p.id === msg.id);
                                    const isStarred = starredMessages.some(s => s.id === msg.id);

                                    // Get sender info from participants list for accurate name
                                    const sender = activeConversation?.participants?.find(p => p.email === msg.sender_email);
                                    const senderName = sender?.name || msg.sender_name || msg.sender_email?.split('@')[0] || 'Unknown';

                                    // Check if this is the first unread message
                                    const isFirstUnread = firstUnreadMessageId && msg.id === firstUnreadMessageId;

                                    return (
                                        <React.Fragment key={msg.id}>
                                            {/* Date Separator (Slack-style) */}
                                            {showDate && (
                                                <div className="flex items-center justify-center my-6 px-4">
                                                    <div className="flex-1 border-t dark:border-slate-700/50 border-slate-200"></div>
                                                    <span className="px-4 py-1 text-xs font-semibold dark:text-slate-400 text-slate-500 dark:bg-slate-800/50 bg-slate-100 rounded-full">
                                                        {formatDateSeparator(msg.created_at)}
                                                    </span>
                                                    <div className="flex-1 border-t dark:border-slate-700/50 border-slate-200"></div>
                                                </div>
                                            )}

                                            {/* Unread Messages Divider (Slack-style) */}
                                            {isFirstUnread && (
                                                <div className="flex items-center justify-center my-4 px-4">
                                                    <div className="flex-1 border-t-2 border-red-500/60"></div>
                                                    <span className="px-3 py-0.5 text-xs font-semibold text-red-500 bg-red-500/10 rounded-full">
                                                        New messages
                                                    </span>
                                                    <div className="flex-1 border-t-2 border-red-500/60"></div>
                                                </div>
                                            )}

                                            {/* Message */}
                                            <div
                                                id={`msg-${msg.id}`}
                                                className={`group relative ${showSender && !isReply ? 'mt-4' : 'mt-0.5'} dark:hover:bg-slate-800/20 hover:bg-slate-100/60 rounded-lg transition-all duration-150 ${msg.isTemp ? 'opacity-70' : ''} ${isPinned ? 'border-l-2 border-l-amber-500 pl-3' : ''} ${isReply ? 'ml-14' : 'px-2 py-1'}`}
                                            >
                                            {/* Thread connector line for replies */}
                                            {isReply && (
                                                <div className="absolute -left-4 top-0 bottom-0 w-4 flex justify-center">
                                                    <div className="w-0.5 h-full bg-slate-600/50"></div>
                                                </div>
                                            )}
                                            <div className={`flex ${isReply ? 'gap-2 pl-4 py-1' : 'gap-2'} ${isMe && !isReply ? 'flex-row-reverse' : ''}`}>
                                                {/* Avatar with hover card - smaller for replies */}
                                                <div className={`${isReply ? 'w-6' : 'w-8'} flex-shrink-0 relative group/avatar`}>
                                                    {(showSender || isReply) && !isMe && (
                                                        <>
                                                            {(() => {
                                                                const avatarUrl = sender?.avatar_url || sender?.avatarUrl || msg.sender_avatar;
                                                                const avatarSize = isReply ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs';
                                                                const senderEmail = sender?.email || msg.sender_email || '';
                                                                const senderCompany = sender?.company || sender?.organization || msg.sender_company || '';

                                                                return (
                                                                    <>
                                                                        {/* Avatar */}
                                                                        {avatarUrl ? (
                                                                            <img
                                                                                src={avatarUrl}
                                                                                alt={senderName}
                                                                                className={`${avatarSize} rounded-lg object-cover shadow-lg bg-slate-800 cursor-pointer`}
                                                                            />
                                                                        ) : (
                                                                            <div className={`${avatarSize} rounded-lg flex items-center justify-center font-bold text-white shadow-lg cursor-pointer ${isMe
                                                                                ? 'bg-gradient-to-br from-blue-600 to-indigo-700'
                                                                                : 'bg-gradient-to-br from-sky-600 to-blue-700'
                                                                                }`}>
                                                                                {senderName[0]?.toUpperCase() || '?'}
                                                                            </div>
                                                                        )}

                                                                        {/* Hover Card */}
                                                                        {!isMe && (
                                                                            <div className="absolute left-0 top-full mt-2 z-50 opacity-0 invisible group-hover/avatar:opacity-100 group-hover/avatar:visible transition-all duration-200 pointer-events-none group-hover/avatar:pointer-events-auto">
                                                                                <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-xl p-3 min-w-[200px] animate-in fade-in slide-in-from-top-1 duration-200">
                                                                                    <div className="flex items-start gap-3">
                                                                                        {avatarUrl ? (
                                                                                            <img src={avatarUrl} alt={senderName} className="h-10 w-10 rounded-lg object-cover" />
                                                                                        ) : (
                                                                                            <div className="h-10 w-10 rounded-lg flex items-center justify-center font-bold text-white bg-gradient-to-br from-sky-600 to-blue-700">
                                                                                                {senderName[0]?.toUpperCase() || '?'}
                                                                                            </div>
                                                                                        )}
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-sm font-semibold text-white truncate">{senderName}</p>
                                                                                            <p className="text-xs text-slate-400 truncate">{senderEmail}</p>
                                                                                            {senderCompany && (
                                                                                                <p className="text-xs text-sky-400 truncate mt-0.5">{senderCompany}</p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    {(sender?.title || sender?.role) && (
                                                                                        <div className="mt-2 pt-2 border-t border-slate-700">
                                                                                            <p className="text-xs text-slate-300">{sender?.title || sender?.role}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </>
                                                    )}
                                                </div>

                                                <div className={`flex-1 min-w-0 ${isMe && !isReply ? 'flex flex-col items-end' : ''}`}>
                                                    {/* For replies: compact inline style (name · time · message on same line) */}
                                                    {isReply ? (
                                                        <div className="flex items-baseline gap-2 flex-wrap">
                                                            <span className="text-xs font-bold dark:text-white text-slate-900">
                                                                {isMe ? 'You' : senderName}
                                                            </span>
                                                            <span className="text-xs text-slate-500" title={getFullTime(msg.created_at)}>
                                                                {getRelativeTime(msg.created_at)}
                                                            </span>
                                                            <span className="text-sm dark:text-slate-200 text-slate-800">
                                                                {msg.text || msg.message || msg.content}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        /* Regular messages: name/time header with bubble below */
                                                        showSender && !isMe && (
                                                            <div className="flex items-baseline gap-2 mb-1 pl-1">
                                                                <span className="text-sm font-bold dark:text-white text-slate-900">
                                                                    {senderName}
                                                                </span>
                                                                <span className="text-xs text-slate-500" title={getFullTime(msg.created_at)}>
                                                                    {getRelativeTime(msg.created_at)}
                                                                </span>
                                                            </div>
                                                        )
                                                    )}

                                                    {/* Message content */}
                                                    <div className={`relative flex flex-col gap-1 max-w-[85%] ${isMe ? 'items-end ml-auto' : 'items-start'}`}>
                                                        {/* Reply indicator - only show for non-threaded replies (orphans) */}
                                                        {msg.reply_to && !isReply && (
                                                            <div
                                                                className="flex items-center gap-2 mb-1 text-xs text-slate-400 cursor-pointer hover:text-sky-400 transition-colors"
                                                                onClick={() => {
                                                                    // Skip scroll when embedded to prevent parent scroll
                                                                    if (isEmbedded) return;
                                                                    const parentEl = document.getElementById(`msg-${msg.reply_to.id}`);
                                                                    if (parentEl) {
                                                                        parentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                        parentEl.classList.add('ring-2', 'ring-sky-500');
                                                                        setTimeout(() => parentEl.classList.remove('ring-2', 'ring-sky-500'), 2000);
                                                                    }
                                                                }}
                                                            >
                                                                <CornerUpLeft size={12} />
                                                                <span>Replying to <span className="font-medium text-sky-400">{msg.reply_to.sender_name}</span></span>
                                                            </div>
                                                        )}

                                                        {/* Pinned/Starred Indicator */}
                                                        {(isPinned || isStarred) && (
                                                            <div className="flex items-center gap-2 mb-1">
                                                                {isPinned && (
                                                                    <span className="flex items-center gap-1 text-[10px] text-amber-500">
                                                                        <Pin size={10} /> Pinned
                                                                    </span>
                                                                )}
                                                                {isStarred && (
                                                                    <span className="flex items-center gap-1 text-[10px] text-yellow-500">
                                                                        <Star size={10} className="fill-current" /> Starred
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Message bubble - skip for replies (text shown inline above) */}
                                                        {!isReply && (msg.text || msg.message || msg.content) && (
                                                            <div className={`inline-block px-4 py-2.5 text-sm leading-relaxed shadow-sm transition-all whitespace-pre-wrap break-words ${isMe
                                                                ? 'bg-gradient-to-br from-sky-600 to-sky-700 text-white rounded-2xl rounded-br-sm'
                                                                : 'dark:bg-slate-800 bg-white dark:text-slate-200 text-slate-800 border dark:border-slate-700 border-slate-200 rounded-2xl rounded-bl-sm'
                                                                }`}>
                                                                {/* Render formatted text with @mentions */}
                                                                {parseFormattedText(msg.text || msg.message || msg.content, activeConversation?.participants)}
                                                                {/* Edited indicator */}
                                                                {msg.edited && (
                                                                    <span className="text-[10px] opacity-60 ml-2" title={msg.edited_at ? `Edited ${getFullTime(msg.edited_at)}` : 'Edited'}>
                                                                        (edited)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Timestamp for my messages */}
                                                        {isMe && !isReply && showSender && (
                                                            <span className="text-xs text-slate-500 mt-1" title={getFullTime(msg.created_at)}>
                                                                {getRelativeTime(msg.created_at)}
                                                            </span>
                                                        )}

                                                        {/* Attachments - always align to end (right) for consistent positioning */}
                                                        {msg.attachments && msg.attachments.length > 0 && (
                                                            <div className="flex flex-col gap-2 mt-1 w-full items-end self-end">
                                                                {msg.attachments.map((att, i) => {
                                                                    const attType = getAttachmentType(att);
                                                                    const attUrl = att.signed_url || att.url || att.signedUrl;

                                                                    // Audio attachment - render audio player
                                                                    if (attType.startsWith('audio/')) {
                                                                        return (
                                                                            <AudioAttachment
                                                                                key={i}
                                                                                src={attUrl}
                                                                                filename={getAttachmentName(att)}
                                                                                fileSize={att.file_size}
                                                                                mimeType={attType}
                                                                                onDownload={() => handleAttachmentPreview(att)}
                                                                            />
                                                                        );
                                                                    }

                                                                    // Video attachment - render video player
                                                                    if (attType.startsWith('video/')) {
                                                                        return (
                                                                            <VideoAttachment
                                                                                key={i}
                                                                                src={attUrl}
                                                                                filename={getAttachmentName(att)}
                                                                                fileSize={att.file_size}
                                                                                mimeType={attType}
                                                                                onDownload={() => handleAttachmentPreview(att)}
                                                                            />
                                                                        );
                                                                    }

                                                                    // Image attachment - render image preview with proper URL fetching
                                                                    if (attType.startsWith('image/')) {
                                                                        return (
                                                                            <ImageAttachment
                                                                                key={i}
                                                                                attachment={att}
                                                                                isMe={isMe}
                                                                                onPreview={handleAttachmentPreview}
                                                                                filename={getAttachmentName(att)}
                                                                                fileSize={att.file_size}
                                                                                formatFileSize={formatFileSize}
                                                                                fetchSignedUrl={ChatService.getAttachmentSignedUrl}
                                                                            />
                                                                        );
                                                                    }

                                                                    // Default attachment button for other file types
                                                                    return (
                                                                        <button
                                                                            key={i}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handleAttachmentPreview(att);
                                                                            }}
                                                                            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm border transition text-left w-full ${isMe
                                                                                ? 'bg-sky-600/20 border-sky-500/30 hover:bg-sky-600/30'
                                                                                : 'dark:bg-slate-900/80 bg-slate-100 dark:border-slate-700/60 border-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800'
                                                                                }`}
                                                                        >
                                                                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isMe ? 'bg-sky-600/40 text-white' : 'dark:bg-slate-800 bg-white dark:text-slate-300 text-slate-500'}`}>
                                                                                <FileText size={16} />
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="truncate font-semibold">{getAttachmentName(att)}</div>
                                                                                <div className="text-xs text-slate-400">
                                                                                    {attType || "File"}
                                                                                    {formatFileSize(att.file_size) ? ` • ${formatFileSize(att.file_size)}` : ""}
                                                                                </div>
                                                                            </div>
                                                                            <span className="text-xs font-semibold text-sky-500 dark:text-sky-300">View</span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {/* Link Previews */}
                                                        {msg.link_previews && msg.link_previews.length > 0 && (
                                                            <div className="mt-1 w-full max-w-sm overflow-hidden">
                                                                {msg.link_previews.map((preview, i) => (
                                                                    <LinkPreview key={i} preview={preview} />
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Thread reply count indicator (Slack-style) */}
                                                        {!isReply && replyCountByRoot.get(msg.id) > 0 && (
                                                            <button
                                                                onClick={() => {
                                                                    // Skip scroll when embedded to prevent parent scroll
                                                                    if (isEmbedded) return;
                                                                    // Scroll to first reply in thread
                                                                    const firstReplyIdx = threadedMessages.findIndex(m => m._isReply && (m.reply_to_id === msg.id || m.replyToId === msg.id || m.reply_to?.id === msg.id));
                                                                    if (firstReplyIdx > -1) {
                                                                        const replyEl = document.getElementById(`msg-${threadedMessages[firstReplyIdx].id}`);
                                                                        replyEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                    }
                                                                }}
                                                                className="flex items-center gap-1.5 mt-2 px-2 py-1 text-xs text-sky-500 hover:text-sky-400 hover:bg-sky-500/10 rounded-md transition-all group/thread"
                                                            >
                                                                <MessageCircle size={14} className="group-hover/thread:scale-110 transition-transform" />
                                                                <span className="font-medium">{replyCountByRoot.get(msg.id)} {replyCountByRoot.get(msg.id) === 1 ? 'reply' : 'replies'}</span>
                                                            </button>
                                                        )}

                                                        {/* Message status indicator (iMessage-style) */}
                                                        {isMe && (
                                                            <div className="flex items-center mt-1">
                                                                {msg.status === 'sending' && (
                                                                    <Loader2 size={14} className="text-slate-500 animate-spin" />
                                                                )}
                                                                {msg.status === 'sent' && (
                                                                    <CheckCheck size={14} className="text-sky-500" />
                                                                )}
                                                                {msg.status === 'failed' && (
                                                                    <span className="text-xs text-red-400">Failed</span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Enhanced Message Actions Toolbar (Rocket.Chat style) */}
                                                        {!msg.isTemp && (
                                                            <div className={`absolute -top-3 ${isMe ? 'left-0' : 'right-0'} opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center gap-0.5 message-actions-menu`}>
                                                                {/* Quick Reactions */}
                                                                <div className="flex items-center dark:bg-slate-800 bg-white rounded-lg shadow-lg border dark:border-slate-700 border-slate-200 p-0.5">
                                                                    {QUICK_REACTIONS.slice(0, 4).map(emoji => (
                                                                        <button
                                                                            key={emoji}
                                                                            onClick={() => handleReaction(msg.id, emoji)}
                                                                            className="p-1.5 hover:bg-slate-700/50 rounded transition-all hover:scale-110"
                                                                            title={`React with ${emoji}`}
                                                                        >
                                                                            <span className="text-sm">{emoji}</span>
                                                                        </button>
                                                                    ))}
                                                                    {/* More Reactions */}
                                                                    <button
                                                                        onClick={() => setShowQuickReactions(showQuickReactions === msg.id ? null : msg.id)}
                                                                        className="p-1.5 hover:bg-slate-700/50 rounded transition-all text-slate-400 hover:text-yellow-500"
                                                                        title="More reactions"
                                                                    >
                                                                        <Smile size={14} />
                                                                    </button>
                                                                </div>

                                                                {/* Reply Button */}
                                                                <button
                                                                    onClick={() => handleReplyToMessage(msg)}
                                                                    className="p-1.5 dark:bg-slate-800 bg-white rounded-lg shadow-lg border dark:border-slate-700 border-slate-200 dark:text-slate-400 text-slate-500 hover:text-sky-500 transition-all hover:scale-105"
                                                                    title="Reply"
                                                                >
                                                                    <Reply size={14} />
                                                                </button>

                                                                {/* More Actions Menu */}
                                                                <div className="relative">
                                                                    <button
                                                                        onClick={() => setMessageActionsMenu(messageActionsMenu?.messageId === msg.id ? null : { messageId: msg.id })}
                                                                        className="p-1.5 dark:bg-slate-800 bg-white rounded-lg shadow-lg border dark:border-slate-700 border-slate-200 dark:text-slate-400 text-slate-500 hover:text-slate-200 transition-all hover:scale-105"
                                                                        title="More actions"
                                                                    >
                                                                        <MoreHorizontal size={14} />
                                                                    </button>

                                                                    {/* Actions Dropdown */}
                                                                    {messageActionsMenu?.messageId === msg.id && (
                                                                        <div className={`absolute ${isMe ? 'left-0' : 'right-0'} top-full mt-1 w-48 dark:bg-slate-800 bg-white rounded-xl shadow-2xl border dark:border-slate-700 border-slate-200 py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200`}>
                                                                            {/* Edit (only for own messages) */}
                                                                            {isMe && (
                                                                                <button
                                                                                    onClick={() => handleEditMessage(msg)}
                                                                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                                >
                                                                                    <Edit3 size={14} /> Edit message
                                                                                </button>
                                                                            )}

                                                                            {/* Copy */}
                                                                            <button
                                                                                onClick={() => handleCopyMessage(msg)}
                                                                                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                            >
                                                                                <Copy size={14} /> Copy text
                                                                            </button>

                                                                            {/* Pin/Unpin */}
                                                                            <button
                                                                                onClick={() => handlePinMessage(msg)}
                                                                                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                            >
                                                                                {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                                                                                {isPinned ? 'Unpin message' : 'Pin message'}
                                                                            </button>

                                                                            {/* Star/Unstar */}
                                                                            <button
                                                                                onClick={() => handleStarMessage(msg)}
                                                                                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                            >
                                                                                <Star size={14} className={isStarred ? 'fill-yellow-500 text-yellow-500' : ''} />
                                                                                {isStarred ? 'Unstar message' : 'Star message'}
                                                                            </button>

                                                                            {/* Reply */}
                                                                            <button
                                                                                onClick={() => handleReplyToMessage(msg)}
                                                                                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                            >
                                                                                <CornerUpLeft size={14} /> Quote reply
                                                                            </button>

                                                                            {/* Forward - show conversation picker */}
                                                                            {conversations.length > 1 && (
                                                                                <div className="relative group/forward">
                                                                                    <button
                                                                                        className="w-full px-3 py-2 text-left text-sm flex items-center justify-between dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition"
                                                                                    >
                                                                                        <span className="flex items-center gap-2">
                                                                                            <Forward size={14} /> Forward to...
                                                                                        </span>
                                                                                        <span className="text-slate-500">›</span>
                                                                                    </button>
                                                                                    {/* Forward submenu */}
                                                                                    <div className="absolute left-full top-0 ml-1 w-48 dark:bg-slate-800 bg-white rounded-lg shadow-xl border dark:border-slate-700 border-slate-200 py-1 hidden group-hover/forward:block z-50">
                                                                                        {conversations
                                                                                            .filter(c => c.id !== activeConversationId)
                                                                                            .slice(0, 5)
                                                                                            .map(conv => (
                                                                                                <button
                                                                                                    key={conv.id}
                                                                                                    onClick={() => handleForwardMessage(msg, conv.id)}
                                                                                                    className="w-full px-3 py-2 text-left text-sm dark:text-slate-300 text-slate-700 hover:bg-slate-700/50 transition truncate"
                                                                                                >
                                                                                                    {getConversationDisplayName(conv, userEmail)}
                                                                                                </button>
                                                                                            ))
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            <div className="border-t dark:border-slate-700 border-slate-200 my-1"></div>

                                                                            {/* Delete (only for own messages) */}
                                                                            {isMe && (
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setMessageToDelete(msg);
                                                                                        setMessageActionsMenu(null);
                                                                                    }}
                                                                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-400 hover:bg-red-500/10 transition"
                                                                                >
                                                                                    <Trash2 size={14} /> Delete message
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Quick Reactions Popup */}
                                                        {showQuickReactions === msg.id && (
                                                            <div className={`absolute -top-12 ${isMe ? 'left-0' : 'right-0'} quick-reactions-bar dark:bg-slate-800 bg-white rounded-xl shadow-2xl border dark:border-slate-700 border-slate-200 p-2 flex gap-1 z-50 animate-in fade-in slide-in-from-bottom-2`}>
                                                                {QUICK_REACTIONS.map(emoji => (
                                                                    <button
                                                                        key={emoji}
                                                                        onClick={() => {
                                                                            handleReaction(msg.id, emoji);
                                                                            setShowQuickReactions(null);
                                                                        }}
                                                                        className="p-2 hover:bg-slate-700/50 rounded-lg transition-all hover:scale-125"
                                                                    >
                                                                        <span className="text-xl">{emoji}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}

                                                    </div>

                                                    {/* Reactions */}
                                                    {msg.reactions && msg.reactions.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {msg.reactions.map((reaction) => (
                                                                <button
                                                                    key={reaction.emoji}
                                                                    onClick={() => handleReaction(msg.id, reaction.emoji)}
                                                                    className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition ${reaction.userReacted
                                                                        ? 'bg-sky-600/30 border border-sky-600/50 text-sky-700 dark:text-white'
                                                                        : 'dark:bg-slate-800/80 bg-white border dark:border-slate-700/50 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 hover:border-sky-600/50'
                                                                        }`}
                                                                >
                                                                    <span>{reaction.emoji}</span>
                                                                    <span className="font-medium">{reaction.count}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        </React.Fragment>
                                    );
                                })}
                                </>
                            )}

                            {/* Typing indicator */}
                            {isTyping && (
                                <div className="flex gap-2 px-2 py-1 animate-in slide-in-from-bottom-2">
                                    <div className="h-8 w-8 rounded-lg dark:bg-slate-700 bg-slate-200 flex items-center justify-center text-xs flex-shrink-0">
                                        <div className="flex gap-0.5">
                                            <span className="w-1 h-1 dark:bg-slate-400 bg-slate-500 rounded-full animate-bounce"></span>
                                            <span className="w-1 h-1 dark:bg-slate-400 bg-slate-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                                            <span className="w-1 h-1 dark:bg-slate-400 bg-slate-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-400 flex items-center">
                                        Someone is typing...
                                    </div>
                                </div>
                            )}

                            {/* Scroll to bottom button (Slack-style with new message indicator) */}
                            {showScrollButton && (
                                <button
                                    onClick={scrollToBottom}
                                    className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 z-10 animate-in fade-in slide-in-from-bottom-4"
                                    title="Scroll to bottom"
                                >
                                    <ArrowDown size={16} />
                                    <span className="text-sm font-medium">Jump to recent</span>
                                </button>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area (Slack/Messenger style) */}
                        <div className="p-4 border-t dark:border-slate-800 border-slate-200 dark:bg-slate-900/50 bg-white/50 backdrop-blur-sm">
                            {/* Reply-to Banner */}
                            {replyingTo && (
                                <div className="mb-2 flex items-center justify-between dark:bg-sky-500/10 bg-sky-50 border border-sky-500/30 rounded-lg px-3 py-2 animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <CornerUpLeft size={14} className="text-sky-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-xs text-sky-500 font-medium">
                                                Replying to {replyingTo.sender_name || replyingTo.sender_email?.split('@')[0]}
                                            </p>
                                            <p className="text-xs text-slate-400 truncate">
                                                {replyingTo.text || replyingTo.message || replyingTo.content}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCancelReply}
                                        className="p-1 hover:bg-slate-700/30 rounded text-slate-400 hover:text-slate-200 transition"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Edit Message Banner */}
                            {editingMessage && (
                                <div className="mb-2 dark:bg-amber-500/10 bg-amber-50 border border-amber-500/30 rounded-lg px-3 py-2 animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Edit3 size={14} className="text-amber-500" />
                                            <span className="text-xs text-amber-500 font-medium">Editing message</span>
                                        </div>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="p-1 hover:bg-slate-700/30 rounded text-slate-400 hover:text-slate-200 transition"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            className="flex-1 dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-lg px-3 py-2 text-sm dark:text-slate-200 text-slate-900 focus:outline-none focus:border-amber-500"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSaveEdit();
                                                } else if (e.key === 'Escape') {
                                                    handleCancelEdit();
                                                }
                                            }}
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={!editText.trim()}
                                            className="px-3 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSendMessage}>
                                <div className="relative dark:bg-slate-950/50 bg-white border dark:border-slate-700/50 border-slate-200 rounded-xl focus-within:border-sky-500/50 focus-within:ring-2 focus-within:ring-sky-500/20 transition-all duration-200 shadow-sm focus-within:shadow-md">

                                    {/* Formatting Toolbar (Rocket.Chat style) */}
                                    {showFormattingBar && (
                                        <div className="flex items-center gap-1 px-3 pt-2 border-b dark:border-slate-700/50 border-slate-200 pb-2">
                                            <button
                                                type="button"
                                                onClick={() => applyFormatting('bold')}
                                                className="p-1.5 rounded hover:bg-slate-700/50 text-slate-400 hover:text-white transition"
                                                title="Bold (Ctrl+B)"
                                            >
                                                <Bold size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => applyFormatting('italic')}
                                                className="p-1.5 rounded hover:bg-slate-700/50 text-slate-400 hover:text-white transition"
                                                title="Italic (Ctrl+I)"
                                            >
                                                <Italic size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => applyFormatting('code')}
                                                className="p-1.5 rounded hover:bg-slate-700/50 text-slate-400 hover:text-white transition"
                                                title="Inline code"
                                            >
                                                <Code size={14} />
                                            </button>
                                            <div className="w-px h-4 bg-slate-700 mx-1"></div>
                                            <span className="text-[10px] text-slate-500">**bold** *italic* `code`</span>
                                        </div>
                                    )}

                                    {/* Mention Suggestions Popup */}
                                    {showMentionPopup && mentionSuggestions.length > 0 && (
                                        <div className="absolute bottom-full left-0 mb-1 w-64 dark:bg-slate-800 bg-white rounded-xl shadow-2xl border dark:border-slate-700 border-slate-200 py-1 z-50 animate-in fade-in slide-in-from-bottom-2">
                                            <p className="px-3 py-1 text-[10px] text-slate-500 uppercase tracking-wide">Mention someone</p>
                                            {mentionSuggestions.map(member => (
                                                <button
                                                    key={member.email}
                                                    type="button"
                                                    onClick={() => insertMention(member)}
                                                    className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-700/50 transition"
                                                >
                                                    <div className="h-6 w-6 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-[10px] font-bold text-white">
                                                        {member.name?.[0] || member.email[0].toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm dark:text-slate-200 text-slate-800 truncate">{member.name || member.email}</p>
                                                        <p className="text-[10px] text-slate-500 truncate">{member.email}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Pending Attachments & Previews */}
                                    {(attachments.length > 0 || pendingPreviews.length > 0) && (
                                        <div className="px-3 pt-3 pb-1 space-y-2">
                                            {attachments.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {attachments.map(att => (
                                                        <div key={att.id} className="relative group dark:bg-slate-800 bg-slate-100 rounded-lg p-2 flex items-center gap-2 border dark:border-slate-700 border-slate-200">
                                                            {getAttachmentType(att).startsWith('image/') ? <ImageIcon size={16} className="text-sky-400" /> : <FileText size={16} className="text-slate-400" />}
                                                            <span className="text-xs dark:text-slate-300 text-slate-700 max-w-[150px] truncate">{getAttachmentName(att)}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeAttachment(att.id)}
                                                                className="absolute -top-2 -right-2 bg-slate-700 hover:bg-red-600 text-slate-300 hover:text-white rounded-full p-1 transition opacity-0 group-hover:opacity-100"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {pendingPreviews.length > 0 && (
                                                <div className="space-y-2 max-w-sm">
                                                    {pendingPreviews.map((preview, i) => (
                                                        <LinkPreview
                                                            key={i}
                                                            preview={preview}
                                                            onRemove={() => removePendingPreview(preview.url)}
                                                            isEditable={true}
                                                        // Pass explicit styles for light/dark mode if the component doesn't handle them automatically,
                                                        // but assuming LinkPreview is handled or will be handled separately.
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Loading Preview Indicator */}
                                    {isGeneratingPreview && (
                                        <div className="px-3 py-1 flex items-center gap-2 text-xs text-slate-500">
                                            <Loader2 size={12} className="animate-spin" />
                                            <span>Generating preview...</span>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-1 p-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            className="hidden"
                                            onChange={handleFileSelect}
                                        />
                                        {/* Formatting Toggle */}
                                        <button
                                            type="button"
                                            onClick={() => setShowFormattingBar(!showFormattingBar)}
                                            className={`p-2 rounded-lg transition ${showFormattingBar
                                                ? 'text-sky-500 bg-sky-500/10'
                                                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                                            }`}
                                            title="Formatting options"
                                        >
                                            <Bold size={18} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="p-2 rounded-lg text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition"
                                            title="Attach file"
                                            disabled={isUploading}
                                        >
                                            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                                        </button>
                                        {/* Voice Recording Button - Hidden for now, will build later */}
                                        {/* Video Recording Button - Hidden for now, will build later */}
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                className={`p-2 rounded-lg transition ${showEmojiPicker
                                                    ? 'dark:bg-slate-800 bg-slate-200 text-yellow-500'
                                                    : 'text-slate-400 hover:text-yellow-500 hover:bg-slate-100 dark:hover:bg-slate-800/50'
                                                    }`}
                                                title="Add emoji"
                                            >
                                                <Smile size={18} />
                                            </button>
                                            {showEmojiPicker && (
                                                <>
                                                    <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)}></div>
                                                    <div className="absolute bottom-12 left-0 z-50 shadow-2xl rounded-xl overflow-hidden border dark:border-slate-700 border-slate-200">
                                                        <Suspense fallback={
                                                            <div className="w-[350px] h-[450px] dark:bg-slate-900 bg-white flex items-center justify-center">
                                                                <Loader2 size={24} className="text-sky-500 animate-spin" />
                                                            </div>
                                                        }>
                                                            <EmojiPicker
                                                                theme="auto"
                                                                onEmojiClick={onEmojiClick}
                                                                searchDisabled={false}
                                                                skinTonesDisabled
                                                                width={350}
                                                                height={450}
                                                            />
                                                        </Suspense>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <textarea
                                            ref={inputRef}
                                            value={messageInput}
                                            onChange={(e) => {
                                                setMessageInput(e.target.value);
                                                // Detect @mentions
                                                handleMentionDetection(e.target.value, e.target.selectionStart);
                                            }}
                                            onPaste={handlePaste}
                                            onKeyDown={(e) => {
                                                // Enter to send, Shift+Enter for new line
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage(e);
                                                }
                                                // Escape to cancel reply/close popups
                                                if (e.key === 'Escape') {
                                                    if (replyingTo) handleCancelReply();
                                                    if (showMentionPopup) setShowMentionPopup(false);
                                                    if (showEmojiPicker) setShowEmojiPicker(false);
                                                }
                                                // Keyboard shortcuts for formatting
                                                if (e.ctrlKey || e.metaKey) {
                                                    if (e.key === 'b') {
                                                        e.preventDefault();
                                                        applyFormatting('bold');
                                                    } else if (e.key === 'i') {
                                                        e.preventDefault();
                                                        applyFormatting('italic');
                                                    }
                                                }
                                            }}
                                            placeholder={`Message ${getConversationDisplayName(activeConversation, userEmail)}...`}
                                            className="flex-1 bg-transparent px-2 py-2 text-sm dark:text-slate-200 text-slate-900 placeholder:text-slate-500 dark:placeholder:text-slate-600 focus:outline-none resize-none max-h-32 overflow-y-auto"
                                            rows={1}
                                            style={{
                                                minHeight: '2.5rem',
                                                height: 'auto'
                                            }}
                                            onInput={(e) => {
                                                // Auto-resize textarea
                                                e.target.style.height = 'auto';
                                                e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            type="submit"
                                            disabled={(!messageInput.trim() && attachments.length === 0) || isSending}
                                            className={`p-2.5 rounded-xl disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center ${
                                                (!messageInput.trim() && attachments.length === 0) || isSending
                                                    ? 'bg-slate-600/50 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500'
                                                    : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white shadow-lg shadow-sky-900/30 hover:scale-105 active:scale-95 animate-pulse-subtle'
                                            }`}
                                            title={isSending ? "Sending..." : "Send message (Enter)"}
                                        >
                                            {isSending ? (
                                                <Loader2 size={18} className="animate-spin" />
                                            ) : (
                                                <Send size={18} className={`transition-transform ${messageInput.trim() || attachments.length > 0 ? 'fill-current translate-x-0.5' : ''}`} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 px-1 flex items-center gap-4">
                                    <span className="flex items-center gap-1.5">
                                        <kbd className="px-1.5 py-0.5 text-[10px] font-semibold dark:bg-slate-800 bg-slate-200 dark:text-slate-300 text-slate-600 rounded border dark:border-slate-700 border-slate-300">Enter</kbd>
                                        <span>to send</span>
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <kbd className="px-1.5 py-0.5 text-[10px] font-semibold dark:bg-slate-800 bg-slate-200 dark:text-slate-300 text-slate-600 rounded border dark:border-slate-700 border-slate-300">Shift + Enter</kbd>
                                        <span>for new line</span>
                                    </span>
                                </p>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                        <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-sky-500/10 to-blue-600/10 flex items-center justify-center mb-4">
                            <MessageSquare size={48} className="text-sky-500/40" />
                        </div>
                        <h3 className="text-xl font-semibold dark:text-white text-slate-900 mb-2">Your Messages</h3>
                        <p className="dark:text-slate-400 text-slate-500 mb-6 max-w-sm">
                            Select a conversation or start a new chat to begin messaging your team
                        </p>
                        <button
                            onClick={() => setIsNewChatOpen(true)}
                            className="px-6 py-3 bg-sky-600 hover:bg-sky-500 text-white rounded-xl font-medium transition-all hover:scale-105 shadow-lg shadow-sky-900/50"
                        >
                            <Plus size={18} className="inline mr-2" />
                            New Message
                        </button>
                    </div>
                )}
            </div>

            {/* Video Message Recorder Modal */}
            {isRecordingVideo && (
                <VideoMessageRecorder
                    onRecordingComplete={handleVideoRecordingComplete}
                    onCancel={() => setIsRecordingVideo(false)}
                />
            )}
        </div>
    );
};

export default TeamChat;
